// CIM Command Center → Sections tab (#/cim/<engagementId>?tab=sections) — the
// 17 sections of one deal's CIM (routes/cim_sections.py; design pr4_7 §6, §7,
// PR 6).
//
//   Tab         one row per section in chapter order, #n = Zach's build order:
//               data readiness, state, the critic's
//               exit label, the lowest page score, the gate dot, Update
//               available, the last build, Build (menu: Build, Revise with
//               instructions, Re-render on vN when the snapshot changed its
//               figures) and Open. Build all, and Build selected over the
//               ticked rows (PR 4c): one chain, one section at a time in
//               Zach's order; its strip shows the sections built, current
//               and left, a stop reason, Continue / Skip and Cancel chain.
//               Approve clean (can_approve): every section whose latest
//               version has nothing to acknowledge, confirmed by name, one
//               approval each on the server; a skip comes back with why.
//               While a build, critique or chain runs, the rows are read
//               again every 5 s (cacheMs: 0); the polling stops when none
//               runs. Assignee: the section's open Atlas task (who, due,
//               overdue in words); a click, or Assign selected over the
//               ticked rows, opens the Assign drawer (person, due date,
//               Unassign). Approving the section completes its task.
//   Workspace   ?tab=sections&section=11&v=2 (deep-linkable): the version
//               picker, page thumbnails, the page image (Page / Review), and
//               Critic, Checks, Numbers, Requests, Versions (compare) and
//               Notes. Approve (every open warning acknowledged), Reopen and
//               Suppress / Unsuppress (with a reason), Revise with
//               instructions, Update from earlier sections (update_because),
//               Re-render on vN (the snapshot changed its figures: no writer,
//               one critic round), Revise from critique, Critique again,
//               Build. A re-rendered version whose G5 fails says Rebuild needed.
//               A running job's progress lines are polled every 5 s.
//   Numbers     building locks them: no Freeze first. A build whose lock is
//               blocked, or has warnings to tick, answers 409 {lock} and the
//               Lock numbers drawer asks once (blocks with Open Ledger and
//               Use numbers vN; warnings to tick), then runs the same build
//               again. Build all asks GET build-lock first, so it is one
//               dialog either way.
//
// Page images are signed URLs that live 10 minutes, fetched per view. Model
// text (critic quotes, why and fix, notes for the banker, gap reasons, the
// revision log) renders only through <window.Markdown strict> or as plain
// text. Versions and runs show as "§11 v2" and "Architecture v3" (the
// payload's refs), never as ids. Section requests are drafts: they are sent
// from the Data room tab. Labels only; timestamps, never spend.
//
// window.CimSectionsTab({ engagementId, phone, chapterOf, onGoTab, onOpenHandoff })
// window.CimSectionWorkspace(…) — one section, opened from the tab
// window.CimSectionsKit — the pure rules (atlas-crm/test/cim_sections.test.mjs)

const SC_STATE = {
  suppressed: ['Suppressed', 'slate'], building: ['Building', 'blue'], not_started: ['Not started', 'slate'],
  draft: ['Draft', 'amber'], approved: ['Approved', 'green'], approved_newer: ['Approved · newer version', 'teal'],
};
const SC_EXIT = { 'Ready for banker': 'green', 'Ready, with critic notes': 'teal', 'Needs banker input': 'amber' };
const SC_JOB = {
  queued: ['Queued', 'slate'], running: ['Running', 'blue'], validating: ['Validating', 'amber'],
  completed: ['Completed', 'green'], failed: ['Failed', 'rose'], cancelled: ['Cancelled', 'slate'],
};
const SC_REVIEW = { scored: ['Scored', 'green'], refused: ['Refused', 'rose'], failed: ['Failed', 'rose'] };
const SC_CHAIN = { running: ['Running', 'blue'], stopped: ['Stopped', 'rose'], completed: ['Completed', 'green'], cancelled: ['Cancelled', 'slate'] };
const SC_CHAIN_STEP = { built: 'Built', skipped: 'Skipped', current: 'Building', stopped: 'Stopped', remaining: 'Waiting' };
// A calculation proposal: the build's status ('proposed' / 'refused'), else its ledger candidate's now.
const SC_CALC = {
  proposed: ['Proposed', 'blue'], refused: ['Refused', 'rose'], approved: ['Approved', 'green'], candidate: ['Unconfirmed', 'slate'],
  overridden: ['Overridden', 'violet'], disputed: ['Disputed', 'rose'], rejected: ['Rejected', 'slate'], superseded: ['Superseded', 'slate'],
};
const SC_REVISION = { applied: ['Applied', 'green'], partially_applied: ['Partially applied', 'amber'], declined: ['Declined', 'slate'] };
const SC_KIND = { generated: 'Built', revised: 'Revised', element: 'Element', rerendered: 'Re-rendered', manual: 'Edited' };
const SC_CRITERIA = {
  P1: 'Action title', P2: 'Proof', P3: 'Buyer question answered', P4: 'Evidence', P5: 'Candor',
  P6: 'Exhibit craft', P7: 'Hierarchy and density', P8: 'Imagery', P9: 'Language', P10: 'Buyer experience',
};
const SC_ITEMS = Object.keys(SC_CRITERIA);
const SC_GATES = ['schema', 'G1', 'G2', 'G3', 'G4', 'G5', 'G6', 'G7', 'G8', 'G9', 'G10'];
// Each check (cim_book/gates.py) in plain words; its code shows only in a tooltip.
const SC_GATE_WORDS = {
  schema: 'Page structure', G1: 'Numbers traced to sources', G2: 'No typed numbers', G3: 'Math adds up',
  G4: 'Consistent basis and period', G5: 'Words match numbers', G6: 'Fits the page', G7: 'No placeholders or links',
  G8: 'No confidential names', G9: 'Native and tagged', G10: 'Deal killers addressed',
};
const scGateWord = (g) => SC_GATE_WORDS[g] || String(g || '');
// A version's or a page's checks as one tooltip: "Checks pass" / "Checks failing: Math adds up (G3), …".
const scGatesLabel = (passed, codes) => (passed ? 'Checks pass'
  : `Checks failing${(codes || []).length ? `: ${codes.map((g) => `${scGateWord(g)} (${g})`).join(', ')}` : ''}`);
const SC_PANES = [['critic', 'Critic'], ['checks', 'Checks'], ['numbers', 'Numbers'], ['requests', 'Requests'], ['versions', 'Versions'], ['notes', 'Notes']];
const SC_TERMINAL = new Set(['completed', 'failed', 'cancelled']);
const SC_EVENT = { status: ['Status', 'var(--text-2)'], tool: ['Read', 'var(--text-3)'], save: ['Saved', 'var(--green-fg)'], check: ['Check', 'var(--amber-fg)'] };
const SC_MAX_INSTRUCTIONS = 2000;
const SC_MIN_REASON = 3;
const SC_IMAGE_STALE_MS = 8 * 60 * 1000;
const SC_PRIORITY = { P0: 'Essential', P1: 'High value', P2: 'Supplemental' };

const _scCard = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10 };
const _scCap = { fontSize: 10.5, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-4)' };
const _scInput = {
  width: '100%', fontSize: 12.5, padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border)',
  background: 'var(--surface)', color: 'var(--text)', boxSizing: 'border-box', fontFamily: 'inherit',
};
const _scLink = { border: 'none', background: 'transparent', padding: 0, font: 'inherit', color: 'var(--accent-text)', cursor: 'pointer', textAlign: 'left' };
const _scMono = { fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--text-3)' };
const _scPlain = { border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', font: 'inherit', color: 'inherit' };
const _scSrOnly = { position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0 };
const _scFloat = { ..._scCard, boxShadow: '0 8px 24px oklch(0.2 0.01 270 / 0.14)', maxWidth: 'min(380px, calc(100vw - 16px))', boxSizing: 'border-box' };

// ── pure rules (window.CimSectionsKit) ─────────────────────────────────────

// apiFetch throws "409 Conflict: {"detail": …}". A string detail is the
// server's sentence; an object (the approve 409's {warnings, acknowledge}) comes back whole.
const _scErr = (e) => {
  const msg = (e && e.message) || '';
  const m = /^(\d{3})\b[^:]*:\s*([\s\S]*)$/.exec(msg);
  if (!m) return { status: null, detail: msg || 'Something went wrong', body: null };
  let detail = m[2].trim();
  let body = null;
  try {
    const j = JSON.parse(detail);
    if (j && j.detail != null) {
      if (typeof j.detail === 'string') detail = j.detail;
      else if (Array.isArray(j.detail)) detail = (j.detail[0] && j.detail[0].msg) || 'Not valid';
      else { body = j.detail; detail = j.detail.message || 'The warnings changed'; }
    }
  } catch (_) { /* not JSON */ }
  return { status: Number(m[1]), detail: detail || `Error ${m[1]}`, body };
};
const _scPlural = (n, one, many) => `${n} ${n === 1 ? one : (many || `${one}s`)}`;
const _scPad = (n) => String(parseInt(n, 10)).padStart(2, '0');
const _scReasonOk = (r) => String(r || '').trim().length >= SC_MIN_REASON;
const _scWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const t = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return d.toDateString() === new Date().toDateString() ? t : `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}, ${t}`;
};
const _scDay = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};
const _scClock = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', second: '2-digit' });
};
const scScore = (s) => (s == null || s === '' ? '—' : Number(s) % 1 ? Number(s).toFixed(1) : String(Number(s)));
const scScoreHue = (s) => (s == null ? 'slate' : s >= 9 ? 'green' : s >= 8 ? 'teal' : 'amber');

// "#/cim/<eid>?tab=sections&section=11&v=2" → { section: '11', v: 2 } (null: the list).
const scParseHash = (hash, eid) => {
  const h = String(hash || '');
  const base = `#/cim/${eid}`;
  if (!(h === base || h.startsWith(`${base}?`))) return null;
  const q = new URLSearchParams(h.split('?')[1] || '');
  if (q.get('tab') !== 'sections') return null;
  const n = parseInt(q.get('section') || '', 10);
  if (!(n >= 1 && n <= 17)) return null;
  const v = parseInt(q.get('v') || '', 10);
  return { section: _scPad(n), v: v >= 1 ? v : null };
};
const scHash = (eid, sel) => `#/cim/${eid}?tab=sections${sel ? `&section=${parseInt(sel.section, 10)}${sel.v ? `&v=${sel.v}` : ''}` : ''}`;

// "§11 v2" / "Architecture v3" from a payload's refs (null: not this deal's, or none).
const scVersionRef = (refs, id) => {
  const r = id && refs && refs.versions ? refs.versions[id] : null;
  return r ? `§${r.section} v${r.version}` : null;
};
const scRunRef = (refs, id) => {
  const r = id && refs && refs.runs ? refs.runs[id] : null;
  return r ? `Architecture v${r.version}` : null;
};

// "v4 · Revised · round 1 · Oct 22, 2:05 PM"
const scVersionLabel = (v) => (v ? [`v${v.version}`, SC_KIND[v.kind] || v.kind, v.round ? `round ${v.round}` : null, _scWhen(v.created_at)]
  .filter(Boolean).join(' · ') : '');

// What an earlier_section diff touched, in words: "2 claims · 1 page".
const _scDiffCount = (d) => {
  if (!d) return '';
  const n = (part) => Object.values((d && d[part]) || {}).reduce((a, x) => a + ((x && x.length) || 0), 0);
  return [['claims', 'claim'], ['tokens', 'figure'], ['page_ids', 'page'], ['risk_flags', 'risk flag']]
    .map(([k, w]) => [n(k), w]).filter(([c]) => c > 0).map(([c, w]) => _scPlural(c, w)).join(' · ');
};

// One version's update_available reasons as lines: [{kind, text, detail}].
const scUpdateLines = (reasons, refs) => (reasons || []).flatMap((r) => {
  if (r.reason === 'snapshot') {
    const b = r.because || [];
    return b.length ? b.map((x) => ({ kind: 'Locked numbers', text: `${x.ref} ${x.change}`, detail: '' }))
      : [{ kind: 'Locked numbers', text: 'Newer locked numbers', detail: '' }];
  }
  if (r.reason === 'earlier_section') {
    const nw = refs && refs.versions && refs.versions[r.new_version_id];
    const od = refs && refs.versions && refs.versions[r.old_version_id];
    const from = r.old_not_built || !r.old_version_id ? 'not built' : od ? `v${od.version}` : 'older version';
    const to = nw ? `v${nw.version}` : 'newer approved version';
    return [{ kind: 'Earlier section', text: `§${r.section} ${from} → ${to}`, detail: _scDiffCount(r.because) }];
  }
  if (r.reason === 'handoff') {
    const a = scRunRef(refs, r.old_run_id);
    const b = scRunRef(refs, r.new_run_id);
    return [{ kind: 'Section plan', text: a && b ? `${a} → ${b.replace('Architecture ', '')}` : b ? `${b} approved` : 'Newer approved section plan', detail: '' }];
  }
  return [{ kind: 'Update', text: String(r.reason || ''), detail: '' }];
});
// A row's reasons: the approved version's (what the book uses) and the latest's, once each.
const scRowUpdates = (row) => {
  const u = (row && row.update_available) || {};
  const out = [];
  const seen = new Set();
  [['approved', row && row.approved], ['latest', row && row.latest]].forEach(([k, v]) => {
    if (!v || seen.has(v.id)) return;
    seen.add(v.id);
    (u[k] || []).forEach((r) => out.push({ ...r, version: v.version }));
  });
  return out;
};

// Why an action is off, as a label ({ok, why}). ctx: the tab's or the
// section's payload (enabled, can_run, can_approve, codename, vertical);
// sec: {suppressed, active_job}; v: {id, approved, scored} or null.
// The skill's status is a label, never a reason; no snapshot isn't one either
// (building locks the numbers).
const scGate = (ctx, sec, action, v) => {
  const no = (why) => ({ ok: false, why });
  const ok = { ok: true, why: null };
  if (!ctx) return no('Loading');
  const s = sec || {};
  if (action === 'approve' || action === 'reopen' || action === 'suppress') {
    if (!ctx.can_approve) return no('Admin or dealmaker');
    if (action === 'suppress') return ok;
    if (s.suppressed) return no('Suppressed');
    if (!v) return no('Not built');
    if (action === 'approve' && v.approved) return no('Approved');
    if (action === 'reopen' && !v.approved) return no('Not approved');
    return ok;
  }
  if (!ctx.enabled) return no('Switched off');
  if (!ctx.can_run) return no('Deal team or admin');
  if (s.suppressed) return no('Suppressed');
  if (action !== 'critique' && (!String(ctx.codename || '').trim() || !ctx.vertical)) return no('Codename and vertical first');
  if (s.active_job) return no('Building');
  if (action === 'revise' && !v) return no('Nothing to revise');
  if (action === 'rerender' && !v) return no('Not built');
  if (action === 'critique' && !v) return no('Not built');
  if (action === 'revise_critique') {
    if (!v) return no('Not built');
    if (!v.scored) return no('No critique to revise from');
  }
  return ok;
};

// The last scored review of a version view (what Revise from critique applies).
const scLastScored = (view) => [...((view && view.reviews) || [])].reverse().find((r) => r.status === 'scored') || null;

// page id → {score, gates: [failing gate ids]} from the last scored review and the gate report.
const scPageMarks = (view) => {
  const out = {};
  const mark = (pid) => { if (!out[pid]) out[pid] = { score: null, gates: [] }; return out[pid]; };
  const r = scLastScored(view);
  ((r && r.critique && r.critique.pages) || []).forEach((p) => {
    const s = p.page_score != null ? p.page_score : p.score;
    if (s != null) mark(String(p.page_id)).score = Number(s);
  });
  const gates = ((view && view.gate_report) || {}).gates || {};
  SC_GATES.forEach((g) => (((gates[g] || {}).failures) || []).forEach((f) => {
    const m = mark(String(f.page || '*'));
    if (!m.gates.includes(g)) m.gates.push(g);
  }));
  return out;
};

// The gate report, G1–G10 in order: failures, warnings, not-checked counts.
const scGateGroups = (report) => {
  const gates = (report && report.gates) || {};
  return SC_GATES.filter((g) => gates[g]).map((g) => {
    const x = gates[g];
    return {
      gate: g, name: x.name || g, passed: x.passed !== false, failures: x.failures || [], warnings: x.warnings || [],
      notChecked: (x.checks || []).filter((c) => c.ran === false).length, checks: (x.checks || []).filter((c) => c.ran !== false).length,
    };
  });
};

// The approver ticked every warning the server listed.
const scAllAcknowledged = (warnings, ticked) => (warnings || []).every((w) => ticked && ticked.has(w.id));

// The rows a poll waits on: "06:<job id>:queued|…", plus a running chain's
// place (empty: nothing runs).
const scActiveKey = (P) => [
  ...((P && P.sections) || []).filter((r) => r.active_job).map((r) => `${r.section}:${r.active_job.id}:${r.active_job.status}`),
  ...(P && P.chain && P.chain.status === 'running' ? [`chain:${P.chain.id}:${P.chain.position}`] : []),
].join('|');

// Build all / Build selected (PR 4c). The strip shows while a chain runs or
// is stopped (a stopped one offers Continue / Skip / Cancel chain).
const scChainLive = (chain) => !!chain && (chain.status === 'running' || chain.status === 'stopped');
// Why Build all (ticked undefined) or Build selected is off: the build gate,
// a chain already running, nothing ticked.
const scChainGate = (P, ticked) => {
  const g = scGate(P, {}, 'build', null);
  if (!g.ok) return g;
  if (P.chain && P.chain.status === 'running') return { ok: false, why: 'Chain running' };
  if (ticked !== undefined && !(ticked && ticked.size)) return { ok: false, why: 'Tick sections' };
  return g;
};
// The ticked sections in Zach's build order (the server sorts them again).
const scTickedOrder = (ticked, order) => (order || []).filter((s) => !!ticked && ticked.has(s));
// The newer snapshot changes figures a version uses (update_available's snapshot reason).
const scSnapshotFlag = (reasons) => (reasons || []).some((r) => r && r.reason === 'snapshot');
// The version a row's Re-render re-prints: the latest when flagged, else the approved one.
const scRerenderOf = (row) => {
  const u = (row && row.update_available) || {};
  if (row && row.latest && scSnapshotFlag(u.latest)) return row.latest;
  if (row && row.approved && scSnapshotFlag(u.approved)) return row.approved;
  return null;
};
const scRerenderLabel = (snap) => (snap && snap.version ? `Re-render on locked v${snap.version}` : 'Re-render');

// Building locks the numbers: a build's 409 {lock} (what blocks the lock, the
// warning kinds to tick), or null for any other refusal.
const scLockOf = (x) => (x && x.status === 409 && x.body && x.body.lock) || null;
// The lock's warnings as the checklist rows ScApproveDrawer ticks ({id, text}).
const scLockWarnings = (lock) => ((lock && lock.warnings) || []).map((w) => ({ id: w.kind, text: w.message }));
// What a started build says about its numbers: a new lock, else nothing.
const scLockedNote = (r) => (r && r.numbers && r.numbers.locked ? `Numbers locked as v${r.numbers.version}` : undefined);
// The lock drawer's confirm for a build of one section: "Re-render §11".
const scLockConfirm = (label, n) => `${String(label || 'build').charAt(0).toUpperCase()}${String(label || 'build').slice(1)} §${n}`;

// Assignments (cim_sections/assign.py): a section's open task is its
// assignment; the payload carries it as {assignee, due_date, overdue}.
// "2026-11-04" → "Nov 4" (a date, not an instant: read in local time).
const scDue = (iso) => {
  const d = iso ? new Date(`${iso}T00:00:00`) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '';
};
const scDueLine = (a) => (a && a.due_date ? `${a.overdue ? 'Overdue' : 'Due'} ${scDue(a.due_date)}` : '');
const _scIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
// The server's default: three business days from today.
const scDefaultDue = (from = new Date()) => {
  const d = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  let left = 3;
  while (left > 0) {
    d.setDate(d.getDate() + 1);
    if (d.getDay() !== 0 && d.getDay() !== 6) left -= 1;
  }
  return _scIso(d);
};
// The picker's people, in the server's order: the deal team, then everyone else.
const scAssignOptions = (people, me) => (people || []).map((p) => ({
  value: p.id, label: p.id === me ? `${p.name} (me)` : p.name, group: p.deal_team ? 'Deal team' : 'Everyone else',
}));
// Who the drawer starts on: the one person the rows share, else (one
// unassigned row) whoever finished that section last.
const scAssignStart = (rows) => {
  const list = rows || [];
  const ids = [...new Set(list.map((r) => (r.assignment ? r.assignment.assignee_id : '')))];
  if (ids.length === 1 && ids[0]) return ids[0];
  if (list.length === 1 && !list[0].assignment) return list[0].last_assignee_id || '';
  return '';
};
// The date it starts on: one assigned row keeps its own, else the default.
const scAssignStartDue = (rows, from) => {
  const list = rows || [];
  return list.length === 1 && list[0].assignment && list[0].assignment.due_date ? list[0].assignment.due_date : scDefaultDue(from);
};
// The request: the due date only when the person changed it (an assigned
// section keeps its own; a new one gets the default).
const scAssignBody = (rows, assignee, due, dueTouched) => ({
  sections: (rows || []).map((r) => r.section), assignee_id: assignee || null, ...(assignee && dueTouched && due ? { due_date: due } : {}),
});
// Why Assign (rows: the ticked rows, or one row) is off.
const scAssignGate = (P, rows) => {
  if (!P) return { ok: false, why: 'Loading' };
  if (!P.can_assign) return { ok: false, why: 'Deal team, dealmaker or admin' };
  if (!(rows && rows.length)) return { ok: false, why: 'Tick sections' };
  if (rows.some((r) => r.suppressed)) return { ok: false, why: 'Suppressed' };
  return { ok: true, why: null };
};

// Earlier sections a version read: [{section, label, provisional, notBuilt}].
const scReadLines = (reads, refs) => (reads || []).map((r) => {
  const ref = r.version_id ? scVersionRef(refs, r.version_id) : null;
  return {
    section: _scPad(r.section), notBuilt: !!r.not_built || !r.version_id, provisional: !!r.provisional,
    label: r.not_built || !r.version_id ? `§${_scPad(r.section)}` : ref || `§${_scPad(r.section)}`,
  };
});

// A word diff's ops ([["=", t] | ["-", t] | ["+", t]]) as runs of the same op.
const scRuns = (ops) => {
  const out = [];
  (ops || []).forEach(([op, t]) => {
    const last = out[out.length - 1];
    if (last && last.op === op) last.text += ` ${t}`;
    else out.push({ op, text: t });
  });
  return out;
};

const scMenuStep = (n, i, key) => {
  if (!n) return null;
  if (key === 'Home') return 0;
  if (key === 'End') return n - 1;
  if (key === 'ArrowDown') return i < 0 ? 0 : (i + 1) % n;
  if (key === 'ArrowUp') return i < 0 ? n - 1 : (i - 1 + n) % n;
  return null;
};
// The pane tabs: Left / Right wrap, Home / End.
const scTabStep = (n, i, key) => {
  if (!n) return null;
  if (key === 'Home') return 0;
  if (key === 'End') return n - 1;
  if (key === 'ArrowRight') return (i + 1) % n;
  if (key === 'ArrowLeft') return (i - 1 + n) % n;
  return null;
};

// An earlier section this version read has a newer approved version: "Update from earlier
// sections" revises it with update_because (the earlier sections' diffs, core §10.3).
const scHasEarlier = (reasons) => (reasons || []).some((r) => r && r.reason === 'earlier_section');

// "Needs banker input": what the banker is asked, as plain lines.
const scAskLines = (exit) => (exit && exit.code === 'needs_input' ? exit.asks || [] : []).map((a) => {
  const page = a.page_id === '*' ? 'Section' : a.page_id;
  if (a.kind === 'gate') return [scGateWord(a.gate), page, a.detail].filter(Boolean).join(' · ');
  if (a.kind === 'finding') return [[a.item, SC_CRITERIA[a.item]].filter(Boolean).join(' '), page].filter(Boolean).join(' · ');
  return String(a.text || '');
}).filter(Boolean);

// Approve clean: the rows with nothing to acknowledge (the server's ``clean``), in build
// order, as the {section, version_id} pairs the POST approves — what the person saw.
const scCleanRows = (P) => {
  const rows = (P && P.sections) || [];
  const by = new Map(rows.map((r) => [r.section, r]));
  return ((P && P.build_order) || rows.map((r) => r.section)).map((s) => by.get(s))
    .filter((r) => r && r.clean && r.latest && r.latest.id);
};
const scCleanPicks = (rows) => (rows || []).map((r) => ({ section: r.section, version_id: r.latest.id }));
const scCleanLine = (rows) => (rows || []).map((r) => `§${r.section} v${r.latest.version}`).join(' · ');
// The POST's answer as a toast: approved count, then each skipped section and why.
const scCleanToast = (out) => {
  const a = ((out && out.approved) || []).length;
  const sk = (out && out.skipped) || [];
  return { type: a ? 'success' : 'info', title: a ? `Approved ${_scPlural(a, 'section')}` : 'Nothing approved',
    body: sk.length ? sk.map((x) => `§${x.section} ${x.why}`).join(' · ') : undefined };
};

window.CimSectionsKit = Object.freeze({
  err: _scErr, gate: scGate, parseHash: scParseHash, hash: scHash, versionRef: scVersionRef, runRef: scRunRef,
  versionLabel: scVersionLabel, updateLines: scUpdateLines, rowUpdates: scRowUpdates, lastScored: scLastScored,
  pageMarks: scPageMarks, gateGroups: scGateGroups, allAcknowledged: scAllAcknowledged, activeKey: scActiveKey,
  readLines: scReadLines, runs: scRuns, score: scScore, scoreHue: scScoreHue, menuStep: scMenuStep, reasonOk: _scReasonOk,
  tabStep: scTabStep, hasEarlier: scHasEarlier, askLines: scAskLines, gateWord: scGateWord, gatesLabel: scGatesLabel,
  chainLive: scChainLive, chainGate: scChainGate, tickedOrder: scTickedOrder, snapshotFlag: scSnapshotFlag,
  rerenderOf: scRerenderOf, rerenderLabel: scRerenderLabel, lockOf: scLockOf, lockWarnings: scLockWarnings,
  lockedNote: scLockedNote, lockConfirm: scLockConfirm,
  cleanRows: scCleanRows, cleanPicks: scCleanPicks, cleanLine: scCleanLine, cleanToast: scCleanToast,
  due: scDue, dueLine: scDueLine, defaultDue: scDefaultDue, assignOptions: scAssignOptions, assignStart: scAssignStart,
  assignStartDue: scAssignStartDue, assignBody: scAssignBody, assignGate: scAssignGate,
});

// ── small pieces ───────────────────────────────────────────────────────────

const ScPill = ({ map, k, dot = false }) => {
  const [label, hue] = map[k] || [String(k || '—').replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase()), 'slate'];
  return <window.Pill hue={hue} sz="sm" dot={dot}>{label}</window.Pill>;
};
const ScExit = ({ label }) => (label ? <window.Pill hue={SC_EXIT[label] || 'slate'} sz="sm">{label}</window.Pill> : null);
const ScScore = ({ score, label = '' }) => (score == null ? null
  : <window.Pill hue={scScoreHue(score)} sz="sm" title={label || undefined}>{scScore(score)}</window.Pill>);
const ScDot = ({ ok, label }) => (
  <span role="img" aria-label={label} title={label}
    style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 999, flexShrink: 0, background: ok ? 'var(--green-fg)' : 'var(--rose-fg)' }} />
);
const ScEmpty = ({ children }) => (
  <div style={{ ..._scCard, padding: '18px 16px', fontSize: 12.5, color: 'var(--text-3)', textAlign: 'center' }}>{children}</div>
);
const ScSection = ({ title, children, right }) => (
  <div style={{ marginTop: 14 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
      <span style={_scCap}>{title}</span>
      <span style={{ flex: 1 }} />
      {right}
    </div>
    {children}
  </div>
);
// Model text: strict markdown only.
const ScMd = ({ children, phone }) => {
  const src = (children == null ? '' : children).toString();
  if (!src.trim()) return null;
  return (
    <div style={{ fontSize: phone ? 14 : 12.5, lineHeight: 1.55, color: 'var(--text-2)', overflowWrap: 'anywhere' }}>
      {window.Markdown
        ? <window.Markdown strict className="md-reader">{src}</window.Markdown>
        : <div style={{ whiteSpace: 'pre-wrap' }}>{src}</div>}
    </div>
  );
};
const ScReason = ({ value, onChange, label = 'Reason', max = 500, rows = 2, phone }) => (
  <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={rows} placeholder={label} aria-label={label} maxLength={max}
    style={{ ..._scInput, resize: 'vertical', minHeight: phone ? 64 : 52, fontSize: phone ? 15 : 12.5 }} />
);

// Menus and boxes go to <body> at position: fixed, so no card's overflow clips
// them or scrolls under them (the list's card scrolls sideways).
const _scLayer = (node) => (window.ReactDOM && window.ReactDOM.createPortal && typeof document !== 'undefined' && document.body
  ? window.ReactDOM.createPortal(node, document.body) : node);

// Where a floating box goes: under its button, above it near the bottom of the
// screen, right-aligned past the middle, always inside the viewport. The first
// render is hidden and measured. A scroll outside it, or a resize, closes it.
const useScFloat = (open, anchor, box, onClose) => {
  const [pos, setPos] = React.useState(null);
  React.useLayoutEffect(() => {
    if (!open) { setPos(null); return; }
    const a = anchor.current;
    if (!a || !a.getBoundingClientRect) return;
    const r = a.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const w = Math.min((box.current && box.current.offsetWidth) || 260, vw - 16);
    const h = (box.current && box.current.offsetHeight) || 0;
    const left = Math.max(8, Math.min(r.left + r.width / 2 > vw / 2 ? r.right - w : r.left, vw - w - 8));
    const up = r.bottom + 4 + h > vh - 8 && r.top - 4 - h >= 8;
    setPos(up ? { left, bottom: vh - r.top + 4 } : { left, top: r.bottom + 4 });
  }, [open]);
  React.useEffect(() => {
    if (!open) return undefined;
    const onScroll = (e) => { if (!(box.current && box.current.contains && box.current.contains(e.target))) onClose(); };
    try {
      window.addEventListener('scroll', onScroll, true);
      window.addEventListener('resize', onClose);
    } catch (_) { return undefined; }
    return () => {
      try { window.removeEventListener('scroll', onScroll, true); window.removeEventListener('resize', onClose); } catch (_) { /* ignore */ }
    };
  }, [open]);
  return pos;
};
const _scAt = (pos) => (pos ? { position: 'fixed', ...pos } : { position: 'fixed', top: 0, left: 0, visibility: 'hidden' });

// A chip or count that opens a small box of lines; Escape closes it and
// focus goes back to its button. On a phone, a bottom sheet. children may be
// a function of close.
const ScPop = ({ trigger, label, children, phone = false }) => {
  const [open, setOpen] = React.useState(false);
  const btn = React.useRef(null);
  const box = React.useRef(null);
  const sheet = !!(phone && window.PhoneSheet);
  const close = () => { setOpen(false); if (btn.current && btn.current.focus) btn.current.focus(); };
  (window.useEscapeKey || (() => {}))(open && !sheet, close);
  const pos = useScFloat(open && !sheet, btn, box, () => setOpen(false));
  const body = typeof children === 'function' ? children(close) : children;
  return (
    <span style={{ display: 'inline-flex' }}>
      <button ref={btn} type="button" aria-expanded={open} aria-label={label} title={label} onClick={() => setOpen((o) => !o)}
        style={{ ..._scPlain, display: 'inline-flex', alignItems: 'center' }}>{trigger}</button>
      {sheet && (
        <window.PhoneSheet open={open} onClose={() => setOpen(false)} title={label}>
          <div style={{ padding: '0 18px 14px', fontSize: 14, color: 'var(--text-2)', lineHeight: 1.5 }}>{body}</div>
        </window.PhoneSheet>
      )}
      {!sheet && open && _scLayer(
        <>
          <div aria-hidden="true" onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 399 }} />
          <div ref={box} role="dialog" aria-label={label}
            style={{ ..._scAt(pos), zIndex: 400, minWidth: 260, ..._scFloat, padding: '8px 10px', fontSize: 12, color: 'var(--text-2)', lineHeight: 1.5 }}>
            {body}
          </div>
        </>,
      )}
    </span>
  );
};

// action: {label, why, onClick}, or a list of them — "Update from earlier
// sections", "Re-render on vN" — under the reasons.
const ScUpdateChip = ({ lines, phone = false, action = null, busy = false }) => {
  if (!lines || !lines.length) return null;
  const acts = [].concat(action || []);
  const title = lines.map((l) => `${l.kind}: ${l.text}${l.detail ? ` (${l.detail})` : ''}`).join('\n');
  return (
    <ScPop label="Newer data" phone={phone} trigger={<window.Pill hue="amber" sz="sm" dot title={title}>Newer data</window.Pill>}>
      {(close) => (
        <>
          {lines.map((l, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '96px minmax(0, 1fr)', gap: 8, padding: '2px 0' }}>
              <span style={{ color: 'var(--text-4)' }}>{l.kind}</span>
              <span style={{ overflowWrap: 'anywhere' }}>{l.text}{l.detail ? <span style={{ color: 'var(--text-4)' }}> · {l.detail}</span> : null}</span>
            </div>
          ))}
          {acts.map((a) => (
            <div key={a.key || a.label} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--border)' }}>
              <window.Btn size="sm" variant="outline" disabled={busy || !!a.why} title={a.why || undefined}
                onClick={() => { close(); a.onClick(); }}>{a.label}</window.Btn>
              {a.why && <span style={{ color: 'var(--text-4)' }}>{a.why}</span>}
            </div>
          ))}
        </>
      )}
    </ScPop>
  );
};

// A menu of actions on a button: arrows move, Escape closes and refocuses the button.
const ScMenu = ({ label, items, phone, variant = 'outline', title }) => {
  const [open, setOpen] = React.useState(false);
  const btn = React.useRef(null);
  const list = React.useRef(null);
  const sheet = !!(phone && window.PhoneSheet && window.PhoneActionList);
  (window.useEscapeKey || (() => {}))(open && !sheet, () => { setOpen(false); if (btn.current) btn.current.focus(); });
  const pos = useScFloat(open && !sheet, btn, list, () => setOpen(false));
  React.useEffect(() => {
    if (!open || sheet || !pos) return;
    const first = list.current && list.current.querySelector && list.current.querySelector('[role="menuitem"]:not([disabled])');
    if (first) first.focus({ preventScroll: true });
  }, [open, !!pos]);
  const pick = (it) => { setOpen(false); if (btn.current && btn.current.focus) btn.current.focus(); it.onClick(); };
  const onKey = (e) => {
    if (e.key === 'Tab') { setOpen(false); return; }
    const els = list.current ? Array.from(list.current.querySelectorAll('[role="menuitem"]:not([disabled])')) : [];
    const next = scMenuStep(els.length, els.indexOf(document.activeElement), e.key);
    if (next == null) return;
    e.preventDefault();
    els[next].focus({ preventScroll: true });
  };
  const shown = items.filter(Boolean);
  if (sheet) {
    return (
      <>
        <button ref={btn} type="button" aria-haspopup="menu" aria-expanded={open} title={title || label} onClick={() => setOpen(true)}
          style={{ ..._scCard, padding: '7px 12px', fontSize: 13.5, cursor: 'pointer', color: 'var(--text)' }}>{label}</button>
        <window.PhoneSheet open={open} onClose={() => setOpen(false)} title={title || label}>
          <window.PhoneActionList items={shown.map((it) => ({ key: it.key, label: it.label, sub: it.why || undefined, disabled: !!it.why, onClick: () => pick(it) }))} />
        </window.PhoneSheet>
      </>
    );
  }
  return (
    <span style={{ display: 'inline-flex' }}>
      <button ref={btn} type="button" aria-haspopup="menu" aria-expanded={open} title={title || label} onClick={() => setOpen((o) => !o)}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, height: phone ? 36 : 28, padding: '0 9px', borderRadius: 7, cursor: 'pointer', font: 'inherit',
          fontSize: phone ? 13.5 : 12, border: `1px solid ${variant === 'ghost' ? 'transparent' : 'var(--border)'}`,
          background: open ? 'var(--surface-hover)' : 'var(--surface)', color: 'var(--text)' }}>
        {label}{window.Icon && window.Icon.ChevronD ? <window.Icon.ChevronD size={11} /> : null}
      </button>
      {open && _scLayer(
        <>
          <div aria-hidden="true" onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 399 }} />
          <div ref={list} role="menu" aria-label={label} onKeyDown={onKey}
            style={{ ..._scAt(pos), zIndex: 400, minWidth: 240, ..._scFloat, padding: 4 }}>
            {shown.map((it) => (
              <button key={it.key} type="button" role="menuitem" disabled={!!it.why} title={it.why || undefined} onClick={() => pick(it)}
                style={{ ..._scLink, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1, width: '100%', padding: '7px 10px', fontSize: 12.5,
                  color: it.why ? 'var(--text-4)' : 'var(--text)', cursor: it.why ? 'default' : 'pointer' }}>
                <span>{it.label}</span>
                {it.why && <span style={{ fontSize: 11 }}>{it.why}</span>}
              </button>
            ))}
          </div>
        </>,
      )}
    </span>
  );
};

// A header action. Off: aria-disabled and still focusable, with its reason read
// out (aria-describedby) and on hover; a click does nothing.
const ScAct = ({ a, id, primary = false, busy = false }) => {
  const off = busy || !!a.why;
  const why = a.why ? `${id}-why` : undefined;
  return (
    <>
      <button type="button" aria-disabled={off ? 'true' : undefined} aria-describedby={why} title={a.why || undefined}
        onClick={off ? undefined : a.onClick} data-primary={primary ? 'true' : undefined}
        style={{ display: 'inline-flex', alignItems: 'center', height: 28, padding: '5px 9px', borderRadius: 7, font: 'inherit', fontSize: 12,
          fontWeight: 500, whiteSpace: 'nowrap', cursor: off ? 'not-allowed' : 'pointer', opacity: off ? 0.5 : 1,
          ...(primary ? { background: 'var(--text)', color: 'var(--bg)', border: '1px solid var(--text)' }
            : { background: 'var(--surface)', color: 'var(--text)', border: '1px solid var(--border)' }) }}>
        {a.label}
      </button>
      {a.why && <span id={why} style={_scSrOnly}>{a.why}</span>}
    </>
  );
};

const ScLive = ({ live, canCancel, onCancel, phone }) => {
  const job = live.job || {};
  const scroller = React.useRef(null);
  React.useEffect(() => {
    const el = scroller.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [live.events.length]);
  return (
    <div style={{ ..._scCard, overflow: 'hidden', borderColor: 'var(--accent)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{job.kind === 'critic' ? 'Critique' : job.mode === 'revise' ? 'Revise' : job.mode === 'rerender' ? 'Re-render' : 'Build'}</span>
        <ScPill map={SC_JOB} k={job.status || 'queued'} dot />
        {job.started_at && <span style={{ fontSize: 12, color: 'var(--text-3)' }}>Started {_scWhen(job.started_at)}</span>}
        <span style={{ flex: 1 }} />
        {canCancel && <window.Btn size="sm" variant="outline" onClick={onCancel}>Cancel</window.Btn>}
      </div>
      <div ref={scroller} role="log" aria-live="polite"
        style={{ maxHeight: phone ? 220 : 180, overflowY: 'auto', padding: '6px 0', fontSize: 12, lineHeight: 1.5 }}>
        {live.events.length === 0 && <div style={{ padding: '4px 12px', color: 'var(--text-4)' }}>Queued</div>}
        {live.events.map((ev) => {
          const [label, color] = SC_EVENT[ev.kind] || [ev.kind, 'var(--text-3)'];
          return (
            <div key={ev.id} style={{ display: 'grid', gridTemplateColumns: phone ? '72px 1fr' : '88px 52px 1fr', gap: 8, padding: '1px 12px' }}>
              <span style={{ color: 'var(--text-4)', fontVariantNumeric: 'tabular-nums' }}>{_scClock(ev.at)}</span>
              {!phone && <span style={{ color, fontWeight: 500 }}>{label}</span>}
              <span style={{ color: 'var(--text-2)', overflowWrap: 'anywhere' }}>{ev.text}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// The chain's current section pulses (idempotent inject, as primitives.jsx's
// shimmer; off under prefers-reduced-motion).
const SC_PULSE_ID = 'atlas-cim-chain-kf';
if (typeof document !== 'undefined' && document.getElementById && document.head && !document.getElementById(SC_PULSE_ID)) {
  const st = document.createElement('style');
  st.id = SC_PULSE_ID;
  st.textContent = '@keyframes atlasCimChainPulse { 0%,100% { opacity: 1 } 50% { opacity: .45 } }'
    + ' @media (prefers-reduced-motion: reduce) { [data-step="current"] { animation: none !important } }';
  document.head.appendChild(st);
}

// Build all / Build selected: the chain's sections in build order (built,
// skipped, current, stopped, waiting), its stop reason, Continue / Skip and
// Cancel chain. onAction(action) → continue | skip | cancel.
const ScChainStrip = ({ chain, canRun, enabled, busy, onAction, phone }) => {
  const running = chain.status === 'running';
  const runWhy = !canRun ? 'Deal team or admin' : !enabled ? 'Switched off' : null;
  const stepStyle = (st) => ({
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: phone ? 34 : 30, height: phone ? 26 : 22,
    padding: '0 6px', borderRadius: 6, fontSize: phone ? 12.5 : 11.5, fontVariantNumeric: 'tabular-nums', fontWeight: st === 'current' ? 650 : 500,
    border: `1px solid ${st === 'current' ? 'var(--accent)' : st === 'stopped' ? 'var(--rose-fg)' : 'var(--border)'}`,
    background: st === 'built' ? 'var(--green-bg)' : st === 'current' ? 'var(--accent-soft)' : st === 'stopped' ? 'var(--rose-bg)' : 'var(--surface)',
    color: st === 'built' ? 'var(--green-fg)' : st === 'current' ? 'var(--accent-text)' : st === 'stopped' ? 'var(--rose-fg)' : 'var(--text-3)',
    textDecoration: st === 'skipped' ? 'line-through' : 'none',
    animation: st === 'current' ? 'atlasCimChainPulse 1.6s ease-in-out infinite' : 'none',
  });
  return (
    <div data-chain={chain.status} style={{ ..._scCard, padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: 8,
      borderColor: running ? 'var(--accent)' : 'var(--rose-fg)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>Build chain</span>
        <ScPill map={SC_CHAIN} k={chain.status} dot />
        <span style={{ fontSize: 12, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums' }}>{`${chain.built}/${chain.total} built`}</span>
        {chain.current && <span style={{ fontSize: 12, color: 'var(--text-2)' }}>{`§${chain.current}`}</span>}
        {chain.requested_by && <span style={{ fontSize: 12, color: 'var(--text-4)' }}>{chain.requested_by}</span>}
        <span style={{ flex: 1 }} />
        {chain.can_continue && (
          <window.Btn size="sm" variant="outline" disabled={busy || !!runWhy} title={runWhy || undefined} onClick={() => onAction('continue')}>Continue</window.Btn>
        )}
        {chain.can_skip && (
          <window.Btn size="sm" variant="outline" disabled={busy || !!runWhy} title={runWhy || undefined} onClick={() => onAction('skip')}>Skip</window.Btn>
        )}
        {chain.can_cancel && (
          <window.Btn size="sm" variant="ghost" disabled={busy || !canRun} title={!canRun ? 'Deal team or admin' : undefined} onClick={() => onAction('cancel')}>Cancel chain</window.Btn>
        )}
      </div>
      {chain.status === 'stopped' && chain.stop_reason && (
        <div role="status" style={{ fontSize: 12.5, color: 'var(--rose-fg)', overflowWrap: 'anywhere' }}>{chain.stop_reason}</div>
      )}
      <ol aria-label="Build order" style={{ display: 'flex', flexWrap: 'wrap', gap: 4, margin: 0, padding: 0, listStyle: 'none' }}>
        {(chain.sections || []).map((s) => (
          <li key={s.section} data-step={s.state} title={`§${s.section}${s.title ? ` ${s.title}` : ''} · ${SC_CHAIN_STEP[s.state] || s.state}`}
            aria-label={`§${s.section} ${SC_CHAIN_STEP[s.state] || s.state}`} aria-current={s.state === 'current' ? 'step' : undefined}
            style={stepStyle(s.state)}>{s.section}</li>
        ))}
      </ol>
    </div>
  );
};

// ── dialogs ────────────────────────────────────────────────────────────────

const ScReasonDrawer = ({ title, confirmLabel, busy, onConfirm, onClose, phone }) => {
  const [reason, setReason] = React.useState('');
  const footer = (
    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', width: '100%' }}>
      <window.Btn size="sm" variant="ghost" onClick={onClose}>Close</window.Btn>
      <window.Btn size="sm" variant="primary" disabled={busy || !_scReasonOk(reason)} onClick={() => onConfirm(reason.trim())}>{confirmLabel}</window.Btn>
    </div>
  );
  return (
    <window.Drawer open onClose={onClose} title={title} footer={footer} width={440} widthKey="cim-sections-reason">
      <ScReason value={reason} onChange={setReason} phone={phone} />
    </window.Drawer>
  );
};

// because: the version read an earlier section that has a newer approved
// version; the tick (on by default) adds update_because to the body.
const ScReviseDrawer = ({ title, busy, onConfirm, onClose, phone, because = false }) => {
  const [text, setText] = React.useState('');
  const [withBecause, setWithBecause] = React.useState(!!because);
  const inc = !!because && withBecause;
  const ok = (text.trim().length > 0 || inc) && text.length <= SC_MAX_INSTRUCTIONS;
  const footer = (
    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center', width: '100%' }}>
      <span style={{ fontSize: 11.5, color: 'var(--text-4)', fontVariantNumeric: 'tabular-nums', marginRight: 'auto' }}>{text.length}/{SC_MAX_INSTRUCTIONS}</span>
      <window.Btn size="sm" variant="ghost" onClick={onClose}>Close</window.Btn>
      <window.Btn size="sm" variant="primary" disabled={busy || !ok} onClick={() => onConfirm(text.trim(), inc)}>Revise</window.Btn>
    </div>
  );
  return (
    <window.Drawer open onClose={onClose} title={title} footer={footer} width={520} widthKey="cim-sections-revise">
      <ScReason value={text} onChange={setText} label="Instructions" max={SC_MAX_INSTRUCTIONS} rows={6} phone={phone} />
      {because && (
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, fontSize: phone ? 14 : 12.5, color: 'var(--text)', cursor: 'pointer' }}>
          <input type="checkbox" checked={withBecause} onChange={(e) => setWithBecause(!!e.target.checked)} />
          Include earlier-section changes
        </label>
      )}
    </window.Drawer>
  );
};
// The revise body: the person's instructions and/or the earlier sections' changes.
const scReviseBody = (fromId, instructions, withBecause) => ({
  mode: 'revise', from_version_id: fromId, ...(instructions ? { instructions } : {}), ...(withBecause ? { update_because: true } : {}),
});

// Approve: one tick per warning the server listed, all required. A 409 hands
// back the fresh list (changed: true), unticked.
const ScApproveDrawer = ({ title, warnings, changed, busy, onConfirm, onClose, phone }) => {
  const [ticked, setTicked] = React.useState(() => new Set());
  React.useEffect(() => { setTicked(new Set()); }, [warnings]);
  const all = scAllAcknowledged(warnings, ticked);
  const toggle = (id) => setTicked((cur) => {
    const next = new Set(cur);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const footer = (
    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', width: '100%' }}>
      <window.Btn size="sm" variant="ghost" onClick={onClose}>Close</window.Btn>
      <window.Btn size="sm" variant="primary" disabled={busy || !all} onClick={() => onConfirm((warnings || []).map((w) => w.id))}
        icon={window.Icon && window.Icon.Check ? <window.Icon.Check size={12} /> : null}>Approve</window.Btn>
    </div>
  );
  return (
    <window.Drawer open onClose={onClose} title={title} footer={footer} width={520} widthKey="cim-sections-approve">
      {changed && (
        <div role="status" style={{ ..._scCard, padding: '8px 12px', marginBottom: 10, fontSize: 12.5, background: 'var(--amber-bg)', color: 'var(--amber-fg)', borderColor: 'transparent' }}>
          Warnings changed
        </div>
      )}
      {(warnings || []).length === 0 ? <div style={{ fontSize: 12.5, color: 'var(--text-3)' }}>No open warnings</div> : (
        <fieldset style={{ border: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <legend style={{ ..._scCap, marginBottom: 6 }}>{_scPlural(warnings.length, 'warning')}</legend>
          {warnings.map((w) => (
            <label key={w.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: phone ? '8px 0' : '5px 0', fontSize: phone ? 14 : 12.5, cursor: 'pointer', color: 'var(--text)' }}>
              <input type="checkbox" checked={ticked.has(w.id)} onChange={() => toggle(w.id)} style={{ marginTop: 3, flexShrink: 0 }} />
              <span style={{ overflowWrap: 'anywhere' }}>{w.text}</span>
            </label>
          ))}
        </fieldset>
      )}
    </window.Drawer>
  );
};

// Building locks the numbers (the build's 409 {lock}, or GET build-lock before
// Build all): the version it locks and its counts, what blocks it (with the
// latest locked version as the way round), the warnings to tick. Confirm runs
// the build again with the ticked kinds.
const ScLockDrawer = ({ title, confirmLabel, lock, changed, busy, onConfirm, onUseLocked, onLedger, onClose, phone }) => {
  const warnings = scLockWarnings(lock);
  const blocks = (lock && lock.blocks) || [];
  const [ticked, setTicked] = React.useState(() => new Set());
  React.useEffect(() => { setTicked(new Set()); }, [lock]);
  const all = scAllAcknowledged(warnings, ticked);
  const c = (lock && lock.counts) || {};
  const num = (x) => Number(x || 0).toLocaleString('en-US');
  const toggle = (id) => setTicked((cur) => {
    const next = new Set(cur);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const footer = (
    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', width: '100%', flexWrap: 'wrap' }}>
      <window.Btn size="sm" variant="ghost" onClick={onClose}>Close</window.Btn>
      {blocks.length > 0 && lock.latest_version && onUseLocked && (
        <window.Btn size="sm" variant="outline" disabled={busy} onClick={onUseLocked}>{`Use numbers v${lock.latest_version}`}</window.Btn>
      )}
      <window.Btn size="sm" variant="primary" disabled={busy || blocks.length > 0 || !all}
        onClick={() => onConfirm(warnings.map((w) => w.id))}>{confirmLabel}</window.Btn>
    </div>
  );
  return (
    <window.Drawer open onClose={onClose} title={title} footer={footer} width={520} widthKey="cim-sections-lock">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {changed && (
          <div role="status" style={{ ..._scCard, padding: '8px 12px', fontSize: 12.5, background: 'var(--amber-bg)', color: 'var(--amber-fg)', borderColor: 'transparent' }}>
            Warnings changed
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: phone ? 14 : 12.5 }}>
          <window.Pill hue="slate" sz="sm" title="Building locks the approved numbers as a new version">{`Numbers v${(lock && lock.next_version) || 1}`}</window.Pill>
          <span style={{ color: 'var(--text-2)' }}>{`${num(c.approved)} approved · ${num(c.candidates)} unconfirmed`}</span>
        </div>
        {blocks.length > 0 && (
          <div role="alert" style={{ ..._scCard, padding: '8px 12px', background: 'var(--rose-bg)', borderColor: 'transparent', display: 'flex', flexDirection: 'column', gap: 6 }}>
            {blocks.map((b, i) => (
              <div key={`${b.kind}-${i}`} style={{ fontSize: phone ? 14 : 12.5, color: 'var(--rose-fg)', overflowWrap: 'anywhere' }}>{b.message}</div>
            ))}
            {onLedger && (
              <div><window.Btn size="sm" variant="outline" onClick={onLedger}>Open Ledger</window.Btn></div>
            )}
          </div>
        )}
        {warnings.length > 0 && (
          <fieldset style={{ border: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <legend style={{ ..._scCap, marginBottom: 6 }}>{_scPlural(warnings.length, 'warning')}</legend>
            {warnings.map((w) => (
              <label key={w.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: phone ? '8px 0' : '5px 0', fontSize: phone ? 14 : 12.5, cursor: blocks.length ? 'default' : 'pointer', color: 'var(--text)' }}>
                <input type="checkbox" checked={ticked.has(w.id)} disabled={blocks.length > 0} onChange={() => toggle(w.id)} style={{ marginTop: 3, flexShrink: 0 }} />
                <span style={{ overflowWrap: 'anywhere' }}>{w.text}</span>
              </label>
            ))}
          </fieldset>
        )}
      </div>
    </window.Drawer>
  );
};

// Assign one section (a row's Assignee cell) or the ticked ones (Assign
// selected): the person and the due date. Unassign shows when any of them is
// assigned. Each section's assignment is an Atlas task.
const ScAssignDrawer = ({ rows, people, me, busy, onConfirm, onClose, phone }) => {
  const [who, setWho] = React.useState(() => scAssignStart(rows));
  const [due, setDue] = React.useState(() => scAssignStartDue(rows));
  const [dueTouched, setDueTouched] = React.useState(false);
  const one = rows.length === 1 ? rows[0] : null;
  const assigned = rows.filter((r) => r.assignment);
  const today = _scIso(new Date());
  const pastDue = dueTouched && due && due < today;
  const footer = (
    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', width: '100%' }}>
      {assigned.length > 0 && (
        <window.Btn size="sm" variant="ghost" disabled={busy} onClick={() => onConfirm(scAssignBody(rows, null))}
          title={assigned.length === rows.length ? undefined : `Unassigns ${_scPlural(assigned.length, 'assigned section')}`}
          style={{ marginRight: 'auto' }}>Unassign</window.Btn>
      )}
      <window.Btn size="sm" variant="ghost" onClick={onClose}>Close</window.Btn>
      <window.Btn size="sm" variant="primary" disabled={busy || !who || pastDue}
        onClick={() => onConfirm(scAssignBody(rows, who, due, dueTouched))}>Assign</window.Btn>
    </div>
  );
  const label = (text, htmlFor) => <label htmlFor={htmlFor} style={{ ..._scCap, display: 'block', marginBottom: 4 }}>{text}</label>;
  const keepTip = assigned.length && !one ? 'Sections already assigned keep their due date unless you change it' : undefined;
  return (
    <window.Drawer open onClose={onClose} title={one ? `Assign §${parseInt(one.section, 10)} ${one.title}` : `Assign ${_scPlural(rows.length, 'section')}`}
      footer={footer} width={440} widthKey="cim-sections-assign">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {!one && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {rows.map((r) => (
              <window.Pill key={r.section} hue="slate" sz="sm"
                title={r.assignment ? `${r.title} · ${r.assignment.assignee || 'Assigned'}` : r.title}>§{parseInt(r.section, 10)}</window.Pill>
            ))}
          </div>
        )}
        <div>
          {label('Assignee')}
          <window.Picker value={who} onChange={(v) => setWho(v || '')} options={scAssignOptions(people, me)} placeholder="Pick a person"
            searchable width="100%" title="Assignee" />
        </div>
        <div title={keepTip}>
          {label('Due', 'sc-assign-due')}
          <input id="sc-assign-due" type="date" value={due} min={today} aria-invalid={pastDue ? 'true' : undefined}
            onChange={(e) => { setDue(e.target.value); setDueTouched(true); }}
            style={{ ..._scInput, fontSize: phone ? 16 : 12.5, fontVariantNumeric: 'tabular-nums' }} />
          {pastDue && <div role="alert" style={{ fontSize: 12, color: 'var(--rose-fg)', marginTop: 4 }}>Pick today or a later date</div>}
        </div>
      </div>
    </window.Drawer>
  );
};

// ── the workspace's panes ──────────────────────────────────────────────────

const ScCriticPane = ({ view, pageId, phone }) => {
  const reviews = (view && view.reviews) || [];
  const [idx, setIdx] = React.useState(reviews.length - 1);
  React.useEffect(() => { setIdx(reviews.length - 1); }, [view]);
  if (!reviews.length) return <ScEmpty>Not critiqued</ScEmpty>;
  const r = reviews[Math.max(0, Math.min(idx, reviews.length - 1))];
  const page = r.status === 'scored' ? (((r.critique || {}).pages) || []).find((p) => String(p.page_id) === String(pageId)) : null;
  const asks = scAskLines(view && view.exit);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {asks.length > 0 && (
        <div style={{ ..._scCard, padding: '8px 10px', background: 'var(--amber-bg)', borderColor: 'transparent' }}>
          <div style={{ ..._scCap, color: 'var(--amber-fg)', marginBottom: 4 }}>Needs banker input</div>
          <ul data-asks="" style={{ margin: 0, paddingLeft: 18, fontSize: phone ? 14 : 12.5, color: 'var(--text)', lineHeight: 1.5 }}>
            {asks.map((a, i) => <li key={i} style={{ overflowWrap: 'anywhere' }}>{a}</li>)}
          </ul>
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <select value={String(idx)} onChange={(e) => setIdx(Number(e.target.value))} aria-label="Review"
          style={{ ..._scInput, width: 'auto', maxWidth: '100%', fontSize: phone ? 14 : 12.5 }}>
          {reviews.map((x, i) => (
            <option key={x.id} value={String(i)}>{`Round ${x.round} · ${x.trigger_kind === 'manual' ? 'Manual' : 'Auto'} · ${_scWhen(x.created_at)}`}</option>
          ))}
        </select>
        <ScPill map={SC_REVIEW} k={r.status} />
        {r.status === 'scored' && <ScScore score={r.min_page_score} label="Lowest page score" />}
        {!view.critic_calibrated && <window.Pill hue="slate" sz="sm">Uncalibrated</window.Pill>}
      </div>
      {r.status === 'scored' && <ScMd phone={phone}>{(r.critique || {}).summary}</ScMd>}
      {r.status === 'scored' && !page && <ScEmpty>No critique for this page</ScEmpty>}
      {page && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12.5 }}>
            <span style={_scMono}>{page.page_id}</span>
            <ScScore score={page.page_score} label="Page score" />
            {page.gate_capped && <window.Pill hue="rose" sz="sm">Capped by a check</window.Pill>}
            {(page.gates_failed || []).map((g, i) => (
              <window.Pill key={i} hue="rose" sz="sm" title={[g.gate, g.detail].filter(Boolean).join(' · ')}>{scGateWord(g.gate)}{g.source === 'observed' ? ' · seen' : ''}</window.Pill>
            ))}
          </div>
          {SC_ITEMS.map((item) => {
            const f = (page.scores || {})[item] || {};
            return (
              <div key={item} style={{ borderTop: '1px solid var(--border)', paddingTop: 6, display: 'flex', flexDirection: 'column', gap: 3 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <window.Pill hue={f.score === 2 ? 'green' : f.score === 1 ? 'amber' : 'rose'} sz="sm">{f.score == null ? '—' : f.score}</window.Pill>
                  <span style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--text)' }}>{item} {SC_CRITERIA[item]}</span>
                  {(f.block_ids || []).length > 0 && <span style={_scMono}>{f.block_ids.join(', ')}</span>}
                </div>
                {f.quote && <div style={{ fontSize: 12.5, color: 'var(--text-2)', fontStyle: 'italic', overflowWrap: 'anywhere' }}>“{f.quote}”</div>}
                <ScMd phone={phone}>{f.why}</ScMd>
                {f.fix && <div style={{ display: 'flex', gap: 6 }}><span style={{ ..._scCap, paddingTop: 2 }}>Fix</span><div style={{ flex: 1, minWidth: 0 }}><ScMd phone={phone}>{f.fix}</ScMd></div></div>}
              </div>
            );
          })}
          {(page.buyer_questions || []).length > 0 && (
            <ScSection title="Buyer questions">
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: 'var(--text-2)' }}>
                {page.buyer_questions.map((q, i) => <li key={i} style={{ overflowWrap: 'anywhere' }}>{q}</li>)}
              </ul>
            </ScSection>
          )}
        </div>
      )}
    </div>
  );
};

const ScChecksPane = ({ view }) => {
  const groups = scGateGroups(view && view.gate_report);
  const pending = ((view && view.version) || {}).pending_refs || [];
  if (!groups.length) return <ScEmpty>No checks yet</ScEmpty>;
  const where = (f) => [f.page, f.block, f.field].filter(Boolean).join(' · ');
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {groups.map((g) => (
        <div key={g.gate} style={{ borderTop: '1px solid var(--border)', paddingTop: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
            <ScDot ok={g.passed} label={g.passed ? 'Passed' : 'Failed'} />
            <span title={g.gate === 'schema' ? g.name : `${g.gate} · ${g.name}`} style={{ fontWeight: 600, color: 'var(--text)', flex: 1, minWidth: 0 }}>{scGateWord(g.gate)}</span>
            {g.notChecked > 0 && <window.Pill hue="slate" sz="sm">Not checked {g.notChecked}</window.Pill>}
            {g.failures.length > 0 && <window.Pill hue="rose" sz="sm">{g.failures.length}</window.Pill>}
          </div>
          {[...g.failures.map((f) => ['fail', f]), ...g.warnings.map((f) => ['warn', f])].map(([sev, f], i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 1, padding: '4px 0 2px 16px', fontSize: 12 }}>
              <span style={{ ..._scMono, color: sev === 'fail' ? 'var(--rose-fg)' : 'var(--amber-fg)' }}>{where(f)}</span>
              <span style={{ color: 'var(--text-2)', overflowWrap: 'anywhere' }}>{f.msg}</span>
            </div>
          ))}
        </div>
      ))}
      {pending.length > 0 && (
        <ScSection title="Pending references">
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>{pending.map((p) => <span key={p} style={_scMono}>{p}</span>)}</div>
        </ScSection>
      )}
    </div>
  );
};

const ScNumbersPane = ({ view, onOpenFact, phone }) => {
  const facts = (view && view.facts_used) || [];
  const evidence = (view && view.evidence) || [];
  const src = (f) => {
    const s = f.source || {};
    if (s.kind === 'file') return [s.file_name, s.locator].filter(Boolean).join(' · ');
    if (s.kind === 'note') return ['Note', s.locator].filter(Boolean).join(' · ');
    return '';
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {!facts.length && <ScEmpty>No figures</ScEmpty>}
      {facts.map((f) => (
        <button key={f.fact_id} type="button" onClick={() => onOpenFact(f.fact_id)}
          style={{ ..._scPlain, display: 'grid', gridTemplateColumns: phone ? 'minmax(0, 1fr) auto' : '92px minmax(0, 1fr) auto', gap: 8, textAlign: 'left',
            padding: phone ? '8px 0' : '5px 0', borderTop: '1px solid var(--border)', alignItems: 'baseline' }}>
          {!phone && <span style={{ fontWeight: 600, color: 'var(--text)', fontVariantNumeric: 'tabular-nums', fontSize: 12.5 }}>{f.display || '—'}</span>}
          <span style={{ minWidth: 0, fontSize: 12, color: 'var(--text-3)', lineHeight: 1.45 }}>
            {phone && <span style={{ display: 'block', fontWeight: 600, color: 'var(--text)', fontSize: 14 }}>{f.display || '—'}</span>}
            <span style={{ display: 'block', color: 'var(--text-2)', overflowWrap: 'anywhere' }}>{[f.key, f.period, f.dim].filter(Boolean).join(' · ')}</span>
            <span style={{ display: 'block', overflowWrap: 'anywhere' }}>{[src(f), f.approved_by].filter(Boolean).join(' · ')}</span>
          </span>
          <window.Pill hue={f.status === 'approved' ? 'green' : 'slate'} sz="sm">{f.status === 'approved' ? 'Approved' : 'Unconfirmed'}</window.Pill>
        </button>
      ))}
      {evidence.length > 0 && (
        <ScSection title="Evidence">
          {evidence.map((e) => (
            <div key={e.code} style={{ borderTop: '1px solid var(--border)', padding: '5px 0', fontSize: 12, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                <span style={_scMono}>{e.code}</span>
                <span style={{ color: 'var(--text)', overflowWrap: 'anywhere', flex: 1 }}>{e.claim}</span>
              </div>
              {e.quote && <div style={{ color: 'var(--text-2)', fontStyle: 'italic', overflowWrap: 'anywhere' }}>“{e.quote}”</div>}
              <div style={{ color: 'var(--text-4)' }}>{[(e.source || {}).label || ((e.source || {}).kind === 'removed' ? 'Removed' : ''), e.locator].filter(Boolean).join(' · ')}</div>
            </div>
          ))}
        </ScSection>
      )}
    </div>
  );
};

const ScRequestsPane = ({ view, onGoRoom, onOpenFact }) => {
  const env = (view && view.envelope) || {};
  const req = (view && view.requests) || {};
  const skipped = new Set(req.skipped || []);
  const calcs = (view && view.calculations) || {};
  const suggested = env.suggested_requests || [];
  const gaps = env.gaps || [];
  const props = env.fact_proposals || [];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', fontSize: 12.5 }}>
        <span><span style={{ color: 'var(--text-4)' }}>Drafts created</span> <b style={{ fontVariantNumeric: 'tabular-nums' }}>{req.created || 0}</b></span>
        <span><span style={{ color: 'var(--text-4)' }}>Skipped</span> <b style={{ fontVariantNumeric: 'tabular-nums' }}>{skipped.size}</b></span>
        <span style={{ flex: 1 }} />
        {onGoRoom && <window.Btn size="sm" variant="outline" onClick={onGoRoom}>Open data room</window.Btn>}
      </div>
      <ScSection title="Requests">
        {!suggested.length && <div style={{ fontSize: 12.5, color: 'var(--text-4)' }}>None</div>}
        {suggested.map((r) => (
          <div key={r.key} style={{ borderTop: '1px solid var(--border)', padding: '5px 0', fontSize: 12, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
              <span style={{ color: 'var(--text)', fontWeight: 500, overflowWrap: 'anywhere', flex: 1, minWidth: 0 }}>{r.title}</span>
              {r.priority && <window.Pill hue={r.priority === 'P0' ? 'violet' : 'slate'} sz="sm">{SC_PRIORITY[r.priority] || r.priority}</window.Pill>}
              <window.Pill hue={skipped.has(r.key) ? 'slate' : 'blue'} sz="sm">{skipped.has(r.key) ? 'Held by another source' : 'Draft'}</window.Pill>
            </div>
            {r.what && <div style={{ color: 'var(--text-2)', overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}>{r.what}</div>}
            <span style={_scMono}>{r.key}</span>
          </div>
        ))}
      </ScSection>
      <ScSection title="Gaps">
        {!gaps.length && <div style={{ fontSize: 12.5, color: 'var(--text-4)' }}>None</div>}
        {gaps.map((g) => (
          <div key={g.id} style={{ borderTop: '1px solid var(--border)', padding: '5px 0', fontSize: 12, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
              <span style={_scMono}>{g.id}</span>
              <span style={_scMono}>{[g.page_id, g.block_id].filter(Boolean).join(' · ')}</span>
              {g.priority && <window.Pill hue={g.priority === 'P0' ? 'violet' : 'slate'} sz="sm">{SC_PRIORITY[g.priority] || g.priority}</window.Pill>}
            </div>
            <div style={{ color: 'var(--text-2)', overflowWrap: 'anywhere' }}>{g.reason}</div>
            {(g.request_key || g.discrepancy) && <span style={_scMono}>{g.request_key || g.discrepancy}</span>}
          </div>
        ))}
      </ScSection>
      <ScSection title="Proposals">
        {!props.length && <div style={{ fontSize: 12.5, color: 'var(--text-4)' }}>None</div>}
        {props.map((p) => {
          const c = p.kind === 'calculation' ? calcs[p.id] : null;
          return (
            <div key={p.id} style={{ borderTop: '1px solid var(--border)', padding: '5px 0', fontSize: 12, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                <span style={_scMono}>{p.id}</span>
                <span style={{ color: 'var(--text)', fontWeight: 500 }}>{[p.key, p.period, p.dim].filter(Boolean).join(' · ')}</span>
                <window.Pill hue="slate" sz="sm">{p.kind === 'calculation' ? 'Calculation' : 'Source figure'}</window.Pill>
                {c && <ScPill map={SC_CALC} k={c.status} />}
                {c && c.fact_id && <button type="button" style={{ ..._scLink, fontSize: 12 }} onClick={() => onOpenFact(c.fact_id, true)}>Open</button>}
              </div>
              {p.value_as_stated && <div style={{ color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>{p.value_as_stated}</div>}
              {p.method && <div style={{ color: 'var(--text-2)', overflowWrap: 'anywhere' }}>{p.method}</div>}
              {p.reason && <div style={{ color: 'var(--text-3)', overflowWrap: 'anywhere' }}>{p.reason}</div>}
              {c && c.reason && <div style={{ color: 'var(--rose-fg)', overflowWrap: 'anywhere' }}>{c.reason}</div>}
            </div>
          );
        })}
      </ScSection>
    </div>
  );
};

const ScWords = ({ ops }) => (
  <span style={{ overflowWrap: 'anywhere', lineHeight: 1.6 }}>
    {scRuns(ops).map((r, i) => (
      <React.Fragment key={i}>
        {i > 0 ? ' ' : ''}
        <span style={r.op === '-' ? { background: 'var(--rose-bg)', color: 'var(--rose-fg)', textDecoration: 'line-through' }
          : r.op === '+' ? { background: 'var(--green-bg)', color: 'var(--green-fg)' } : { color: 'var(--text-2)' }}
          data-op={r.op}>{r.text}</span>
      </React.Fragment>
    ))}
  </span>
);

const ScVersionsPane = ({ sec, current, onPick, compare, onCompare, thumbsOf, phone }) => {
  const versions = (sec && sec.versions) || [];
  const others = versions.filter((v) => current && v.id !== current.id);
  const [other, setOther] = React.useState('');
  React.useEffect(() => {
    const parent = current && others.find((v) => v.id === current.parent_version_id);
    setOther((parent || others[0] || {}).id || '');
  }, [current && current.id, versions.length]);
  const data = compare && compare.data;
  const thumbA = data ? thumbsOf(data.from.id) : null;
  const thumbB = data ? thumbsOf(data.to.id) : null;
  const thumb = (set, pid) => ((set || []).find((p) => p.page_id === pid) || {}).url;
  // "Touched" is the newer version's changes against its own parent: shown only when that is the older one.
  const child = !!data && (versions.find((v) => v.id === data.to.id) || {}).parent_version_id === data.from.id;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {versions.map((v) => {
        const on = current && v.id === current.id;
        return (
          <button key={v.id} type="button" onClick={() => onPick(v)} aria-current={on ? 'true' : undefined}
            style={{ ..._scPlain, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', textAlign: 'left', padding: phone ? '9px 6px' : '6px 6px',
              borderTop: '1px solid var(--border)', background: on ? 'var(--accent-soft)' : 'transparent', borderRadius: 6 }}>
            <span style={{ fontWeight: 600, color: 'var(--text)', minWidth: 26, fontSize: 12.5 }}>v{v.version}</span>
            <span style={{ fontSize: 12, color: 'var(--text-2)' }}>{[SC_KIND[v.kind] || v.kind, v.round ? `round ${v.round}` : null].filter(Boolean).join(' · ')}</span>
            <ScDot ok={!!v.gates_passed} label={scGatesLabel(!!v.gates_passed, v.failing)} />
            <ScScore score={v.min_score} label="Lowest page score" />
            <ScExit label={v.exit_label} />
            {v.rebuild_needed && <window.Pill hue="rose" sz="sm">Rebuild needed</window.Pill>}
            {v.approved && <window.Pill hue="green" sz="sm">Approved</window.Pill>}
            <span style={{ flex: 1 }} />
            <span style={{ fontSize: 11.5, color: 'var(--text-4)' }}>{[_scWhen(v.created_at), v.created_by].filter(Boolean).join(' · ')}</span>
          </button>
        );
      })}
      {others.length > 0 && current && (
        <ScSection title="Compare">
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12.5, color: 'var(--text-2)' }}>v{current.version} with</span>
            <select value={other} onChange={(e) => setOther(e.target.value)} aria-label="Compare with"
              style={{ ..._scInput, width: 'auto', fontSize: phone ? 14 : 12.5 }}>
              {others.map((v) => <option key={v.id} value={v.id}>{scVersionLabel(v)}</option>)}
            </select>
            <window.Btn size="sm" variant="outline" disabled={!other || (compare && compare.loading)} onClick={() => onCompare(other)}>Compare</window.Btn>
          </div>
          {compare && compare.err && <div style={{ fontSize: 12.5, color: 'var(--rose-fg)', marginTop: 6 }}>{compare.err}</div>}
          {compare && compare.loading && <div style={{ marginTop: 8 }}><window.SkeletonRows rows={3} /></div>}
          {data && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8, fontSize: 12 }}>
              <div style={{ fontWeight: 600, color: 'var(--text)' }}>v{data.from.version} → v{data.to.version}</div>
              {(data.pages.added || []).length > 0 && <div><span style={{ color: 'var(--text-4)' }}>Pages added</span> <span style={_scMono}>{data.pages.added.join(', ')}</span></div>}
              {(data.pages.removed || []).length > 0 && <div><span style={{ color: 'var(--text-4)' }}>Pages removed</span> <span style={_scMono}>{data.pages.removed.join(', ')}</span></div>}
              {!(data.pages.changed || []).length && !(data.pages.added || []).length && !(data.pages.removed || []).length && (
                <div style={{ color: 'var(--text-4)' }}>No page changes</div>
              )}
              {(data.pages.changed || []).map((c) => (
                <div key={c.page_id} style={{ borderTop: '1px solid var(--border)', paddingTop: 6, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <span style={_scMono}>{c.page_id}</span>
                  {(thumb(thumbA, c.page_id) || thumb(thumbB, c.page_id)) && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                      {[[thumbA, data.from.version], [thumbB, data.to.version]].map(([set, n]) => (
                        <figure key={n} style={{ margin: 0 }}>
                          {thumb(set, c.page_id)
                            ? <img src={thumb(set, c.page_id)} alt={`v${n} ${c.page_id}`} style={{ width: '100%', borderRadius: 4, border: '1px solid var(--border)', display: 'block' }} />
                            : <div style={{ fontSize: 11.5, color: 'var(--text-4)' }}>—</div>}
                          <figcaption style={{ fontSize: 11, color: 'var(--text-4)' }}>v{n}</figcaption>
                        </figure>
                      ))}
                    </div>
                  )}
                  {(c.blocks_added || []).length > 0 && <div><span style={{ color: 'var(--text-4)' }}>Blocks added</span> <span style={_scMono}>{c.blocks_added.join(', ')}</span></div>}
                  {(c.blocks_removed || []).length > 0 && <div><span style={{ color: 'var(--text-4)' }}>Blocks removed</span> <span style={_scMono}>{c.blocks_removed.join(', ')}</span></div>}
                  {(c.blocks_changed || []).map((b) => (
                    <div key={b.block_id} style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      <span style={{ ..._scMono, color: 'var(--text-2)' }}>{b.block_id}</span>
                      {(b.fields || []).map((f) => (
                        <div key={f.field} style={{ display: 'grid', gridTemplateColumns: phone ? 'minmax(0, 1fr)' : '120px minmax(0, 1fr)', gap: 6, paddingLeft: 8 }}>
                          <span style={_scMono}>{f.field}</span>
                          {f.ops ? <ScWords ops={f.ops} /> : (
                            <span style={{ color: 'var(--text-2)', overflowWrap: 'anywhere' }}>
                              <span data-op="-" style={{ color: 'var(--rose-fg)', textDecoration: 'line-through' }}>{JSON.stringify(f.before)}</span>
                              {' → '}
                              <span data-op="+" style={{ color: 'var(--green-fg)' }}>{JSON.stringify(f.after)}</span>
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              ))}
              {child && (data.touched || []).length > 0 && (
                <div><span style={{ color: 'var(--text-4)' }}>Touched</span> <span style={_scMono}>{data.touched.map((t) => [t.page_id, t.block_id].filter(Boolean).join('/')).join(', ')}</span></div>
              )}
            </div>
          )}
        </ScSection>
      )}
    </div>
  );
};

const ScNotesPane = ({ view, row, onOpenHandoff, phone }) => {
  const env = (view && view.envelope) || {};
  const v = (view && view.version) || {};
  const notes = env.notes_for_banker || [];
  const log = env.revision_log || [];
  const mine = row && row.readiness;
  const theirs = env.readiness;
  const reads = scReadLines(view && view.reads, view && view.refs);
  const run = scRunRef(view && view.refs, v.handoff_run_id);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <ScSection title="Notes for the banker">
        {!notes.length && <div style={{ fontSize: 12.5, color: 'var(--text-4)' }}>None</div>}
        {notes.map((n, i) => <div key={i} style={{ borderTop: i ? '1px solid var(--border)' : 'none', padding: '4px 0' }}><ScMd phone={phone}>{n}</ScMd></div>)}
      </ScSection>
      {log.length > 0 && (
        <ScSection title="Revision log">
          {log.map((x, i) => (
            <div key={i} data-revision="" style={{ borderTop: i ? '1px solid var(--border)' : 'none', padding: '5px 0', fontSize: 12, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                <span style={_scMono}>{x.finding || ''}</span>
                {x.action && <ScPill map={SC_REVISION} k={x.action} />}
                <span style={_scMono}>{[x.page_id, (x.block_ids || []).join(', ')].filter(Boolean).join(' · ')}</span>
              </div>
              {x.change && <div style={{ color: 'var(--text-2)', overflowWrap: 'anywhere' }}>{x.change}</div>}
              {x.reason && <div style={{ color: 'var(--text-3)', overflowWrap: 'anywhere' }}>{x.reason}</div>}
            </div>
          ))}
        </ScSection>
      )}
      <ScSection title="Readiness">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12.5 }}>
          {mine && (
            <div><span style={{ color: 'var(--text-4)' }}>Atlas</span> <b style={{ fontVariantNumeric: 'tabular-nums' }}>{mine.present}/{mine.required}</b>
              {(mine.missing || []).length > 0 && <span style={{ color: 'var(--text-3)' }}> · {mine.missing.map((m) => m.block).join(', ')}</span>}</div>
          )}
          {theirs && (
            <div><span style={{ color: 'var(--text-4)' }}>Writer</span> <b style={{ fontVariantNumeric: 'tabular-nums' }}>{theirs.present_blocks}/{theirs.required_blocks}</b>
              {(theirs.missing || []).length > 0 && <span style={{ color: 'var(--text-3)' }}> · {theirs.missing.map((m) => m.block).join(', ')}</span>}</div>
          )}
          {!mine && !theirs && <span style={{ color: 'var(--text-4)' }}>—</span>}
        </div>
      </ScSection>
      <ScSection title="Earlier sections read">
        {!reads.length && <div style={{ fontSize: 12.5, color: 'var(--text-4)' }}>None</div>}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {reads.map((r) => (
            <span key={r.section} style={{ display: 'inline-flex', gap: 4, alignItems: 'center', fontSize: 12.5, color: 'var(--text)' }}>
              {r.label}
              {r.notBuilt ? <window.Pill hue="slate" sz="sm">Not built</window.Pill> : r.provisional ? <window.Pill hue="amber" sz="sm">Provisional</window.Pill> : null}
            </span>
          ))}
        </div>
      </ScSection>
      <ScSection title="Section plan">
        {v.handoff_run_id ? (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12.5, flexWrap: 'wrap' }}>
            <span style={{ color: 'var(--text)' }}>{run || 'Architecture run'}</span>
            {v.handoff_review && <window.Pill hue={v.handoff_review === 'approved' ? 'green' : 'slate'} sz="sm">{v.handoff_review === 'approved' ? 'Approved' : 'Draft'}</window.Pill>}
            {onOpenHandoff && run && <button type="button" style={{ ..._scLink, fontSize: 12.5 }} onClick={() => onOpenHandoff(v.handoff_run_id)}>Open</button>}
          </div>
        ) : <div style={{ fontSize: 12.5, color: 'var(--text-4)' }}>None</div>}
      </ScSection>
    </div>
  );
};

// ── the workspace ──────────────────────────────────────────────────────────

const CimSectionWorkspace = ({ engagementId, section, versionNo = null, ctx, row = null, chapter = null, phone = false,
  onBack, onVersion, onChanged, onGoTab, onOpenHandoff }) => {
  const { useState, useEffect, useRef, useCallback } = React;
  const api = window.AtlasApi;
  const toast = (t) => window.pushToast && window.pushToast(t);
  const [sec, setSec] = useState(null);
  const [secErr, setSecErr] = useState(null);
  const [view, setView] = useState(null);
  const [viewErr, setViewErr] = useState(null);
  const [imgs, setImgs] = useState({});           // `${vid}:${mode}` → {pages, at} | {err}
  const [mode, setMode] = useState('critic');
  const [pageId, setPageId] = useState(null);
  const [pane, setPane] = useState('critic');
  const [live, setLive] = useState(null);         // {jobId, job, events}
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState(null);     // approve | reopen | suppress | unsuppress | revise
  const [warn, setWarn] = useState({ list: [], changed: false });
  const [lockAsk, setLockAsk] = useState(null);   // {lock, body, label, changed}: the numbers need locking first
  const [compare, setCompare] = useState(null);   // {loading, data, err}
  const [fact, setFact] = useState(null);         // {id, snapshot}
  const [zoom, setZoom] = useState(false);        // phone: the page image full size in a sheet
  const liveRef = useRef(live);
  liveRef.current = live;
  const finished = useRef(new Set());
  const seq = useRef({ sec: 0, view: 0 });
  const imgsRef = useRef(imgs);
  imgsRef.current = imgs;
  const n = section;

  const versions = (sec && sec.versions) || [];
  const current = (versionNo ? versions.find((v) => v.version === versionNo) : null)
    || versions.find((v) => sec && v.id === sec.latest_version_id) || versions[0] || null;
  const vid = current ? current.id : null;

  const loadSec = useCallback(() => {
    const my = ++seq.current.sec;
    return api.cimSection(engagementId, n)
      .then((r) => { if (my === seq.current.sec) { setSec(r); setSecErr(null); } return r; })
      .catch((e) => { if (my === seq.current.sec) setSecErr(_scErr(e)); return null; });
  }, [engagementId, n]);
  const loadView = useCallback((id) => {
    if (!id) return Promise.resolve(null);
    const my = ++seq.current.view;
    return api.cimSectionVersion(engagementId, n, id)
      .then((r) => { if (my === seq.current.view) { setView(r); setViewErr(null); } return r; })
      .catch((e) => { if (my === seq.current.view) setViewErr(_scErr(e).detail); return null; });
  }, [engagementId, n]);
  const loadImages = useCallback((id, m, force = false) => {
    if (!id) return Promise.resolve(null);
    const key = `${id}:${m}`;
    const have = imgsRef.current[key];
    if (!force && have && (have.loading || (have.pages && Date.now() - have.at < SC_IMAGE_STALE_MS))) return Promise.resolve(have);
    setImgs((c) => ({ ...c, [key]: { ...(c[key] || {}), loading: true } }));
    return api.cimSectionImages(engagementId, n, id, m)
      .then((r) => { const x = { pages: (r && r.pages) || [], at: Date.now() }; setImgs((c) => ({ ...c, [key]: x })); return x; })
      .catch((e) => { setImgs((c) => ({ ...c, [key]: { err: _scErr(e).detail, at: Date.now() } })); return null; });
  }, [engagementId, n]);

  useEffect(() => {
    setSec(null); setSecErr(null); setView(null); setImgs({}); setLive(null); setCompare(null); setDialog(null); setFact(null); setLockAsk(null);
    finished.current = new Set();
    loadSec();
  }, [engagementId, n]);
  // An unknown v in the link: the latest instead.
  useEffect(() => {
    if (sec && versionNo && !versions.some((v) => v.version === versionNo) && onVersion) onVersion(null);
  }, [sec, versionNo]);
  useEffect(() => {
    setView(null); setViewErr(null); setCompare(null); setPageId(null); setZoom(false);
    if (vid) { loadView(vid); loadImages(vid, 'thumb'); }
  }, [vid]);
  useEffect(() => { if (vid) loadImages(vid, mode); }, [vid, mode]);

  // A job in flight (started here, from the tab, by another person) → the live panel.
  useEffect(() => {
    const j = sec && sec.active_job;
    if (!j || finished.current.has(j.id)) return;
    setLive((cur) => (cur && cur.jobId === j.id ? cur : { jobId: j.id, job: j, events: [] }));
  }, [sec]);

  const finish = useCallback((job) => {
    if (!job || finished.current.has(job.id)) return;
    finished.current.add(job.id);
    setLive((cur) => (cur && cur.jobId === job.id ? null : cur));
    const what = job.kind === 'critic' ? 'Critique' : 'Build';
    if (job.status === 'completed') toast({ type: 'success', title: `§${n} ${what.toLowerCase()} finished` });
    else if (job.status === 'failed') toast({ type: 'error', title: `§${n} ${what.toLowerCase()} failed`, body: job.error || undefined });
    else toast({ type: 'info', title: `§${n} ${what.toLowerCase()} cancelled` });
    // A new version becomes the one shown (unless the link names one); a critique adds a review to this one.
    loadSec().then(() => { if (vid) loadView(vid); });
    if (onChanged) onChanged();
  }, [loadSec, loadView, vid]);

  // Progress lines every 5 s while a job runs; stops when it ends (or this closes).
  window.usePolling(async () => {
    const cur = liveRef.current;
    if (!cur) return 'idle';
    const last = cur.events.length ? cur.events[cur.events.length - 1].id : 0;
    const r = await api.cimLedgerJob(engagementId, cur.jobId, { after: last, eventsOnly: true });
    const now = liveRef.current;
    if (!now || now.jobId !== cur.jobId) return 'gone';
    const evs = (r && r.events) || [];
    setLive((c) => (c && c.jobId === cur.jobId ? { ...c, job: { ...c.job, status: r.status, error: r.error }, events: [...c.events, ...evs.filter((e) => !c.events.some((x) => x.id === e.id))] } : c));
    if (r && SC_TERMINAL.has(r.status)) finish({ ...cur.job, id: cur.jobId, status: r.status, error: r.error });
    return `${r ? r.status : ''}:${evs.length ? evs[evs.length - 1].id : last}`;
  }, {
    active: !!live, key: live ? live.jobId : null, first: 5000, stepAfter: 60 * 60 * 1000,
    giveUpAfter: 0, immediate: true, signature: (s) => s,
  });

  const started = (r, job) => {
    setLive({ jobId: r.job_id, job: { id: r.job_id, status: r.status || 'queued', ...job }, events: [] });
    loadSec();
    if (onChanged) onChanged();
  };
  const refused = (title, e) => {
    const x = _scErr(e);
    toast({ type: 'error', title, body: x.detail });
    if (x.status === 409 || x.status === 404) loadSec();
  };
  // Numbers that need locking first come back as a 409 {lock}: ask once
  // (ScLockDrawer), then run the same build with what was ticked.
  const build = async (body, label) => {
    setBusy(true);
    try {
      const r = await api.cimSectionBuild(engagementId, n, body);
      toast({ type: 'success', title: `§${n} ${label} started`, body: scLockedNote(r) });
      setDialog(null);
      setLockAsk(null);
      started(r, { kind: 'section_build', mode: body.mode });
    } catch (e) {
      const lock = scLockOf(_scErr(e));
      if (lock) {
        setDialog(null);
        setLockAsk((cur) => ({ lock, body, label, changed: !!cur }));
      } else refused(`Could not start the ${label}`, e);
    } finally {
      setBusy(false);
    }
  };
  const critique = async () => {
    if (!current) return;
    setBusy(true);
    try {
      const r = await api.cimSectionCritique(engagementId, n, current.id);
      toast({ type: 'success', title: `Critique of §${n} v${current.version} started` });
      started(r, { kind: 'critic', mode: 'full' });
    } catch (e) {
      refused('Could not start the critique', e);
    } finally {
      setBusy(false);
    }
  };
  const cancel = async () => {
    const cur = liveRef.current;
    if (!cur) return;
    const ok = window.showConfirm ? await window.showConfirm({ title: 'Cancel this job?', confirmLabel: 'Cancel job', cancelLabel: 'Keep running', danger: true }) : true;
    if (!ok) return;
    try {
      const j = await api.cimLedgerCancel(engagementId, cur.jobId);
      finish({ ...cur.job, ...j, id: cur.jobId });
    } catch (e) {
      refused('Could not cancel', e);
    }
  };
  const approval = async (body, done) => {
    setBusy(true);
    try {
      await api.cimSectionApproval(engagementId, n, body);
      toast({ type: 'success', title: done });
      setDialog(null);
      await loadSec();
      if (vid) loadView(vid);
      if (onChanged) onChanged();
    } catch (e) {
      const x = _scErr(e);
      if (body.action === 'approve' && x.status === 409 && x.body && Array.isArray(x.body.warnings)) {
        setWarn({ list: x.body.warnings, changed: true });
      } else {
        refused('Could not save', e);
        if (x.status === 409) setDialog(null);
      }
    } finally {
      setBusy(false);
    }
  };

  const wrap = (body) => <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>{body}</div>;
  const back = (
    <button type="button" onClick={onBack} style={{ ..._scLink, display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12.5 }}>
      {window.Icon && window.Icon.ChevronL ? <window.Icon.ChevronL size={12} /> : '‹'}Sections
    </button>
  );
  if (secErr && !sec) {
    return wrap(<>{back}<ScEmpty>{secErr.detail}</ScEmpty></>);
  }
  if (!sec) return wrap(<>{back}<window.SkeletonRows rows={8} /></>);

  const c = { ...(ctx || {}), enabled: sec.enabled, can_run: sec.can_run, can_approve: sec.can_approve };
  const scored = !!scLastScored(view);
  const vinfo = current && view && view.version && view.version.id === current.id
    ? { id: current.id, approved: !!view.version.approved, scored } : current ? { id: current.id, approved: !!current.approved, scored } : null;
  const g = {
    build: scGate(c, sec, 'build', null),
    revise: scGate(c, sec, 'revise', vinfo),
    critique: scGate(c, sec, 'critique', vinfo),
    reviseCritique: scGate(c, sec, 'revise_critique', vinfo),
    rerender: scGate(c, sec, 'rerender', vinfo),
    approve: scGate(c, sec, 'approve', vinfo),
    reopen: scGate(c, sec, 'reopen', vinfo),
    suppress: scGate(c, sec, 'suppress', null),
  };
  const thumbs = (vid && imgs[`${vid}:thumb`]) || {};
  const big = (vid && imgs[`${vid}:${mode}`]) || {};
  const env = (view && view.envelope) || {};
  const order = (thumbs.pages && thumbs.pages.length ? thumbs.pages.map((p) => p.page_id) : (env.pages || []).map((p) => p.id));
  const pid = pageId && order.includes(pageId) ? pageId : order[0] || null;
  const at = order.indexOf(pid);
  const marks = scPageMarks(view);
  const shown = (big.pages || []).find((p) => p.page_id === pid);
  const alt = shown ? `${mode === 'review' ? 'Review' : 'Page'} ${shown.page_no} ${shown.page_id}` : '';
  const updates = scUpdateLines(view && view.update_available, view && view.refs);
  const earlier = scHasEarlier(view && view.update_available);
  const restated = scSnapshotFlag(view && view.update_available);
  const isApproved = !!(vinfo && vinfo.approved);
  const goPage = (id) => {
    setPageId(id);
    const m = imgsRef.current[`${vid}:${mode}`];
    if (m && m.at && Date.now() - m.at > SC_IMAGE_STALE_MS) loadImages(vid, mode, true);
  };
  const imgFailed = (m) => {
    const x = imgsRef.current[`${vid}:${m}`];
    if (x && !x.loading && x.at && Date.now() - x.at > 30 * 1000) loadImages(vid, m, true);
  };
  const openApprove = () => { setWarn({ list: (view && view.warnings) || [], changed: false }); setDialog('approve'); };

  const actions = [
    { key: 'approve', label: 'Approve', why: g.approve.why || (!view ? 'Loading' : null), onClick: openApprove },
    isApproved ? { key: 'reopen', label: 'Reopen', why: g.reopen.why, onClick: () => setDialog('reopen') } : null,
    { key: 'revise', label: 'Revise with instructions', why: g.revise.why, onClick: () => setDialog('revise') },
    earlier ? { key: 'update', label: 'Update from earlier sections', why: g.revise.why,
      onClick: () => build(scReviseBody(current.id, null, true), 'update') } : null,
    restated ? { key: 'rerender', label: scRerenderLabel(c.snapshot), why: g.rerender.why,
      onClick: () => build({ mode: 'rerender', from_version_id: current.id }, 're-render') } : null,
    { key: 'critique', label: 'Critique again', why: g.critique.why, onClick: critique },
    { key: 'revise_critique', label: 'Revise from critique', why: g.reviseCritique.why,
      onClick: () => build({ mode: 'revise', from_version_id: current.id, from_critique: true }, 'revise') },
    { key: 'build', label: 'Build', why: g.build.why, onClick: () => build({ mode: 'full' }, 'build') },
    sec.suppressed ? { key: 'unsuppress', label: 'Unsuppress', why: g.suppress.why, onClick: () => setDialog('unsuppress') }
      : { key: 'suppress', label: 'Suppress', why: g.suppress.why, onClick: () => setDialog('suppress') },
  ].filter(Boolean);
  const btn = (a) => <ScAct key={a.key} a={a} id={`sc-${n}-${a.key}`} busy={busy} primary={a.key === 'approve' && !a.why} />;

  const header = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {back}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: phone ? 17 : 16, fontWeight: 650, color: 'var(--text)', overflowWrap: 'anywhere' }}>§{n} {sec.title}</h2>
        {chapter && chapter.label && chapter.label !== '—' && <span style={{ fontSize: 12.5, color: 'var(--text-3)' }}>{chapter.label}</span>}
        {row && row.assignment && (
          <span title={`Assigned to ${row.assignment.assignee || 'someone'}`}
            style={{ fontSize: 12.5, color: row.assignment.overdue ? 'var(--rose-fg)' : 'var(--text-3)', fontWeight: row.assignment.overdue ? 600 : 400 }}>
            {row.assignment.assignee || 'Assigned'} · {scDueLine(row.assignment)}
          </span>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {versions.length > 0 && (
          <select value={current ? String(current.version) : ''} aria-label="Version"
            onChange={(e) => onVersion && onVersion(Number(e.target.value) === (versions.find((v) => v.id === sec.latest_version_id) || {}).version ? null : Number(e.target.value))}
            style={{ ..._scInput, width: 'auto', maxWidth: phone ? '100%' : 340, fontSize: phone ? 14 : 12.5 }}>
            {versions.map((v) => <option key={v.id} value={String(v.version)}>{scVersionLabel(v)}</option>)}
          </select>
        )}
        <ScPill map={SC_STATE} k={sec.state} dot />
        {view && view.exit && <ScExit label={view.exit.label} />}
        {isApproved && sec.state !== 'approved' && <window.Pill hue="green" sz="sm">Approved</window.Pill>}
        {view && view.version && view.version.status === 'preliminary' && <window.Pill hue="amber" sz="sm">Preliminary</window.Pill>}
        {view && view.version && view.version.rebuild_needed && <window.Pill hue="rose" sz="sm">Rebuild needed</window.Pill>}
        <ScUpdateChip lines={updates} phone={phone} busy={busy} action={actions.filter((a) => a.key === 'update' || a.key === 'rerender')} />
        {sec.suppressed && sec.suppressed_reason && <span style={{ fontSize: 12, color: 'var(--text-3)', overflowWrap: 'anywhere' }}>{sec.suppressed_reason}</span>}
      </div>
      {phone ? (
        // Approve as a button when it can be done; else in the sheet, with its reason.
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {!actions[0].why && <window.Btn size="sm" variant="primary" disabled={busy} onClick={actions[0].onClick}>{actions[0].label}</window.Btn>}
          <ScMenu label="Actions" title={`§${n} actions`} items={actions.filter((a) => a.key !== 'approve' || !!a.why)} phone />
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          {actions.filter((a) => a.key !== 'suppress' && a.key !== 'unsuppress').map(btn)}
          <ScMenu label="More" variant="ghost" items={actions.filter((a) => a.key === 'suppress' || a.key === 'unsuppress')} />
        </div>
      )}
    </div>
  );

  const thumbStrip = (
    <div role="list" aria-label="Pages"
      style={phone ? { display: 'flex', gap: 8, overflowX: 'auto', WebkitOverflowScrolling: 'touch', margin: '0 -16px', padding: '0 16px 4px' }
        : { display: 'flex', flexDirection: 'column', gap: 8 }}>
      {thumbs.err && <div style={{ fontSize: 12, color: 'var(--rose-fg)' }}>{thumbs.err}</div>}
      {order.map((id, i) => {
        const t = (thumbs.pages || []).find((p) => p.page_id === id);
        const m = marks[id] || { score: null, gates: [] };
        const on = id === pid;
        return (
          <div key={id} role="listitem" style={{ flexShrink: 0, width: phone ? 120 : '100%' }}>
            <button type="button" onClick={() => goPage(id)} aria-current={on ? 'true' : undefined} aria-label={`Page ${t ? t.page_no : i + 1} ${id}`}
              style={{ ..._scPlain, width: '100%', display: 'flex', flexDirection: 'column', gap: 4, padding: 4, borderRadius: 8,
                border: `2px solid ${on ? 'var(--accent)' : 'transparent'}`, background: on ? 'var(--accent-soft)' : 'transparent' }}>
              {t ? <img src={t.url} alt="" onError={() => imgFailed('thumb')} style={{ width: '100%', borderRadius: 4, border: '1px solid var(--border)', display: 'block', background: 'var(--surface)' }} />
                : <div style={{ width: '100%', aspectRatio: '16 / 9', borderRadius: 4, border: '1px solid var(--border)' }} />}
              <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 }}>
                <span style={{ color: 'var(--text-3)' }}>{t ? t.page_no : i + 1}</span>
                <ScDot ok={!m.gates.length} label={scGatesLabel(!m.gates.length, m.gates)} />
                <span style={{ flex: 1 }} />
                <ScScore score={m.score} label="Page score" />
              </span>
            </button>
          </div>
        );
      })}
    </div>
  );

  const centre = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <div role="group" aria-label="Image" style={{ display: 'inline-flex', border: '1px solid var(--border)', borderRadius: 7, overflow: 'hidden' }}>
          {[['critic', 'Page'], ['review', 'Review']].map(([k, label]) => (
            <button key={k} type="button" aria-pressed={mode === k} onClick={() => setMode(k)}
              style={{ ..._scPlain, padding: phone ? '7px 14px' : '4px 12px', fontSize: phone ? 13.5 : 12, fontWeight: mode === k ? 600 : 500,
                background: mode === k ? 'var(--accent-soft)' : 'var(--surface)', color: mode === k ? 'var(--accent-text)' : 'var(--text-2)' }}>{label}</button>
          ))}
        </div>
        <span style={{ flex: 1 }} />
        {pid && <span style={_scMono}>{pid}</span>}
        <window.Btn size="sm" variant="ghost" disabled={at <= 0} onClick={() => goPage(order[at - 1])}>Prev</window.Btn>
        <window.Btn size="sm" variant="ghost" disabled={at < 0 || at >= order.length - 1} onClick={() => goPage(order[at + 1])}>Next</window.Btn>
      </div>
      <div data-image="" style={{ ..._scCard, overflow: 'auto', background: 'var(--surface-2, var(--surface))', minHeight: phone ? 180 : 240 }}>
        {big.err ? <div style={{ padding: 16, fontSize: 12.5, color: 'var(--rose-fg)' }}>{big.err}</div>
          : shown && phone && window.PhoneSheet ? (
            <button type="button" onClick={() => setZoom(true)} aria-label={`Zoom ${alt}`} style={{ ..._scPlain, display: 'block', width: '100%' }}>
              <img src={shown.url} alt={alt} onError={() => imgFailed(mode)} style={{ width: '100%', height: 'auto', display: 'block' }} />
            </button>
          ) : shown ? <img src={shown.url} alt={alt} onError={() => imgFailed(mode)} style={{ width: '100%', height: 'auto', display: 'block' }} />
            : big.loading || !big.pages ? <div style={{ padding: 16 }}><window.SkeletonRows rows={4} /></div>
              : <div style={{ padding: 16, fontSize: 12.5, color: 'var(--text-4)' }}>No image</div>}
      </div>
      {phone && shown && window.PhoneSheet && (
        // Full size: pan both ways, pinch where the browser allows it.
        <window.PhoneSheet open={zoom} onClose={() => setZoom(false)} title={`${mode === 'review' ? 'Review' : 'Page'} ${shown.page_no}`} height="92%" maxHeight="92%">
          <div data-zoom="" style={{ height: '100%', overflow: 'auto', touchAction: 'pan-x pan-y pinch-zoom', WebkitOverflowScrolling: 'touch', background: 'var(--surface-2, var(--surface))' }}>
            <img src={shown.url} alt={alt} onError={() => imgFailed(mode)}
              style={{ display: 'block', maxWidth: 'none', height: 'auto', width: shown.w ? `${shown.w}px` : 'auto' }} />
          </div>
        </window.PhoneSheet>
      )}
    </div>
  );

  const tabId = (k) => `sc-${n}-tab-${k}`;
  const panelId = `sc-${n}-panel`;
  const onTabKey = (e) => {
    const keys = SC_PANES.map(([k]) => k);
    const next = scTabStep(keys.length, keys.indexOf(pane), e.key);
    if (next == null) return;
    e.preventDefault();
    setPane(keys[next]);
    try { const el = document.getElementById(tabId(keys[next])); if (el) el.focus(); } catch (_) { /* ignore */ }
  };
  const paneTabs = (
    <div role="tablist" aria-label="Details" onKeyDown={onTabKey}
      style={{ display: 'flex', gap: phone ? 6 : 0, overflowX: 'auto', WebkitOverflowScrolling: 'touch', borderBottom: phone ? 'none' : '1px solid var(--border)',
        margin: phone ? '0 -16px' : 0, padding: phone ? '0 16px 2px' : 0, scrollbarWidth: 'none' }}>
      {SC_PANES.map(([k, label]) => {
        const on = pane === k;
        return phone ? (
          <button key={k} id={tabId(k)} type="button" role="tab" aria-selected={on} aria-controls={panelId} tabIndex={on ? 0 : -1} onClick={() => setPane(k)}
            style={{ flexShrink: 0, padding: '7px 12px', borderRadius: 999, fontSize: 13.5, cursor: 'pointer', whiteSpace: 'nowrap',
              border: `1px solid ${on ? 'var(--accent)' : 'var(--border)'}`, background: on ? 'var(--accent-soft)' : 'var(--surface)',
              color: on ? 'var(--accent-text)' : 'var(--text-2)', fontWeight: on ? 600 : 500 }}>{label}</button>
        ) : (
          <button key={k} id={tabId(k)} type="button" role="tab" aria-selected={on} aria-controls={panelId} tabIndex={on ? 0 : -1} onClick={() => setPane(k)}
            style={{ padding: '0 7px', height: 30, border: 'none', background: 'transparent', fontSize: 12, fontWeight: on ? 600 : 500, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
              color: on ? 'var(--text)' : 'var(--text-3)', borderBottom: on ? '2px solid var(--text)' : '2px solid transparent', marginBottom: -1 }}>{label}</button>
        );
      })}
    </div>
  );
  const thumbsOf = (id) => ((imgs[`${id}:thumb`] || {}).pages) || [];
  const runCompare = async (otherId) => {
    const o = versions.find((v) => v.id === otherId);
    if (!o || !current) return;
    const [a, b] = o.version < current.version ? [o, current] : [current, o];
    setCompare({ loading: true });
    try {
      const d = await api.cimSectionCompare(engagementId, n, a.id, b.id);
      setCompare({ data: d });
      loadImages(a.id, 'thumb'); loadImages(b.id, 'thumb');
    } catch (e) {
      setCompare({ err: _scErr(e).detail });
    }
  };
  const openFact = (id, live_ = false) => setFact({ id, snapshot: live_ ? null : (view && view.version && view.version.snapshot_version) || null });

  let paneBody = null;
  if (viewErr) paneBody = <div style={{ fontSize: 12.5, color: 'var(--rose-fg)' }}>{viewErr}</div>;
  else if (!view && pane !== 'versions') paneBody = <window.SkeletonRows rows={5} />;
  else if (pane === 'critic') paneBody = <ScCriticPane view={view} pageId={pid} phone={phone} />;
  else if (pane === 'checks') paneBody = <ScChecksPane view={view} />;
  else if (pane === 'numbers') paneBody = <ScNumbersPane view={view} onOpenFact={openFact} phone={phone} />;
  else if (pane === 'requests') paneBody = <ScRequestsPane view={view} onOpenFact={openFact} onGoRoom={onGoTab ? () => onGoTab('room', { filter: 'approve' }) : null} />;
  else if (pane === 'versions') {
    paneBody = <ScVersionsPane sec={sec} current={current} phone={phone} compare={compare} onCompare={runCompare} thumbsOf={thumbsOf}
      onPick={(v) => onVersion && onVersion(v.id === sec.latest_version_id ? null : v.version)} />;
  } else paneBody = <ScNotesPane view={view} row={row} phone={phone} onOpenHandoff={onOpenHandoff ? (runId) => onOpenHandoff(runId, parseInt(n, 10)) : null} />;

  const liveBox = live && <ScLive live={live} canCancel={!!sec.can_run} onCancel={cancel} phone={phone} />;
  const empty = !versions.length;
  const body = empty ? (
    <ScEmpty>{sec.suppressed ? 'Suppressed' : 'Not built'}</ScEmpty>
  ) : phone ? (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {thumbStrip}
      {centre}
      {paneTabs}
      <div id={panelId} role="tabpanel" aria-labelledby={tabId(pane)}>{paneBody}</div>
    </div>
  ) : (
    // Three columns, each scrolling itself inside a pane the page's height.
    <div style={{ display: 'grid', gridTemplateColumns: '150px minmax(0, 1fr) minmax(360px, 440px)', gap: 12, height: 'calc(100vh - 290px)', minHeight: 460 }}>
      <div style={{ height: '100%', overflowY: 'auto', paddingRight: 2 }}>{thumbStrip}</div>
      <div style={{ height: '100%', overflowY: 'auto' }}>{centre}</div>
      <div style={{ ..._scCard, height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <div style={{ padding: '0 10px', flexShrink: 0 }}>{paneTabs}</div>
        <div id={panelId} role="tabpanel" aria-labelledby={tabId(pane)} style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '10px 12px 14px' }}>{paneBody}</div>
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {header}
      {liveBox}
      {body}
      {dialog === 'approve' && current && (
        <ScApproveDrawer title={`Approve §${n} v${current.version}`} warnings={warn.list} changed={warn.changed} busy={busy} phone={phone}
          onClose={() => setDialog(null)}
          onConfirm={(ids) => approval({ action: 'approve', version_id: current.id, acknowledge: ids }, `§${n} v${current.version} approved`)} />
      )}
      {dialog === 'reopen' && current && (
        <ScReasonDrawer title={`Reopen §${n} v${current.version}`} confirmLabel="Reopen" busy={busy} phone={phone} onClose={() => setDialog(null)}
          onConfirm={(reason) => approval({ action: 'reopen', version_id: current.id, reason }, `§${n} reopened`)} />
      )}
      {(dialog === 'suppress' || dialog === 'unsuppress') && (
        <ScReasonDrawer title={`${dialog === 'suppress' ? 'Suppress' : 'Unsuppress'} §${n}`} confirmLabel={dialog === 'suppress' ? 'Suppress' : 'Unsuppress'}
          busy={busy} phone={phone} onClose={() => setDialog(null)}
          onConfirm={(reason) => approval({ action: dialog, reason }, `§${n} ${dialog === 'suppress' ? 'suppressed' : 'unsuppressed'}`)} />
      )}
      {dialog === 'revise' && current && (
        <ScReviseDrawer title={`Revise §${n} v${current.version}`} busy={busy} phone={phone} because={earlier} onClose={() => setDialog(null)}
          onConfirm={(instructions, withBecause) => build(scReviseBody(current.id, instructions, withBecause), 'revise')} />
      )}
      {lockAsk && (
        <ScLockDrawer title="Lock numbers" confirmLabel={scLockConfirm(lockAsk.label, n)} lock={lockAsk.lock} changed={lockAsk.changed}
          busy={busy} phone={phone} onClose={() => setLockAsk(null)}
          onConfirm={(kinds) => build({ ...lockAsk.body, acknowledge: kinds }, lockAsk.label)}
          onUseLocked={() => build({ ...lockAsk.body, use_locked: true }, lockAsk.label)}
          onLedger={onGoTab ? () => { setLockAsk(null); onGoTab('ledger'); } : null} />
      )}
      {fact && window.CimFactDrawer && (
        <window.CimFactDrawer engagementId={engagementId} factId={fact.id} snapshot={fact.snapshot} canReview={false} phone={phone}
          onClose={() => setFact(null)} onOpen={(id) => setFact((f) => ({ ...f, id }))} onChanged={() => {}} />
      )}
    </div>
  );
};

// ── the tab ────────────────────────────────────────────────────────────────

const CimSectionsTab = ({ engagementId, phone = false, chapterOf = null, onGoTab, onOpenHandoff }) => {
  const { useState, useEffect, useRef, useCallback } = React;
  const api = window.AtlasApi;
  const toast = (t) => window.pushToast && window.pushToast(t);
  const [P, setP] = useState(null);
  const [err, setErr] = useState(null);
  const [open, setOpen] = useState(() => { try { return scParseHash(window.location.hash, engagementId); } catch (_) { return null; } });
  const [busy, setBusy] = useState(null);           // the section being started
  const [revise, setRevise] = useState(null);       // the row a Revise with instructions is for
  const [ticked, setTicked] = useState(() => new Set()); // Build selected, Assign selected
  const [chainBusy, setChainBusy] = useState(false);
  const [lockAsk, setLockAsk] = useState(null);     // {lock, title, confirmLabel, changed, retry}: lock the numbers first
  const [assigning, setAssigning] = useState(null); // the rows the Assign drawer is for
  const [assignBusy, setAssignBusy] = useState(false);
  const seq = useRef(0);
  const prevActive = useRef(new Map());
  const prevChain = useRef(null);

  const load = useCallback(() => {
    const my = ++seq.current;
    return api.cimSections(engagementId)
      .then((r) => {
        if (my !== seq.current) return r;
        // A job that was running and is gone: say how it ended.
        const now = new Map(((r && r.sections) || []).filter((x) => x.active_job).map((x) => [x.section, x.active_job.id]));
        prevActive.current.forEach((jid, s) => {
          if (now.get(s) === jid) return;
          const row = ((r && r.sections) || []).find((x) => x.section === s);
          const last = row && row.last_job;
          if (last && last.id === jid) {
            const what = last.kind === 'critic' ? 'critique' : 'build';
            toast(last.status === 'completed' ? { type: 'success', title: `§${s} ${what} finished` }
              : last.status === 'failed' ? { type: 'error', title: `§${s} ${what} failed`, body: last.error || undefined }
                : { type: 'info', title: `§${s} ${what} ${last.status}` });
          }
        });
        prevActive.current = now;
        // A chain that was running and now isn't: say how it ended (a person's cancel says so itself).
        const was = prevChain.current;
        const ch = r && r.chain;
        if (was && ch && was.id === ch.id && was.status === 'running' && ch.status !== was.status) {
          if (ch.status === 'completed') toast({ type: 'success', title: 'Build chain finished' });
          else if (ch.status === 'stopped') toast({ type: 'error', title: 'Build chain stopped', body: ch.stop_reason || undefined });
        }
        prevChain.current = ch ? { id: ch.id, status: ch.status } : null;
        setP(r); setErr(null);
        return r;
      })
      .catch((e) => { if (my === seq.current) setErr(_scErr(e)); return null; });
  }, [engagementId]);

  useEffect(() => { setP(null); setErr(null); prevActive.current = new Map(); prevChain.current = null; setTicked(new Set()); load(); }, [engagementId]);

  // The section and version live in the hash (deep links); replaceState, like the tabs.
  const writeHash = (sel) => {
    try {
      const cur = window.location.hash || '';
      const base = `#/cim/${engagementId}`;
      if (!(cur === base || cur.startsWith(`${base}?`))) return;
      const next = scHash(engagementId, sel);
      if (cur !== next) window.history.replaceState(null, '', next);
    } catch (_) { /* ignore */ }
  };
  const go = (sel) => { setOpen(sel); writeHash(sel); };
  useEffect(() => {
    const onHash = () => {
      const sel = scParseHash(window.location.hash, engagementId);
      setOpen((cur) => (cur && sel && cur.section === sel.section && cur.v === sel.v) || (!cur && !sel) ? cur : sel);
    };
    try { window.addEventListener('hashchange', onHash); } catch (_) { return undefined; }
    return () => { try { window.removeEventListener('hashchange', onHash); } catch (_) { /* ignore */ } };
  }, [engagementId]);

  // Back on the list: read it again (the workspace may have built or approved).
  const wasOpen = useRef(!!open);
  useEffect(() => {
    if (wasOpen.current && !open) load();
    wasOpen.current = !!open;
  }, [!!open]);

  // Every 5 s while a build, critique or chain runs (the workspace polls its own job).
  const activeKey = scActiveKey(P);
  window.usePolling(async () => {
    const r = await load();
    return scActiveKey(r) || 'idle';
  }, {
    active: !open && !!activeKey, key: activeKey || null, first: 5000, stepAfter: 60 * 60 * 1000,
    giveUpAfter: 0, immediate: false, signature: (s) => s,
  });

  // A 409 {lock} (the numbers need locking first) opens ScLockDrawer; its
  // confirm runs the same build again with what was ticked (``retry``).
  const askLock = (lock, title, confirmLabel, retry) => setLockAsk((cur) => ({ lock, title, confirmLabel, changed: !!cur, retry }));
  const start = async (row, body, label) => {
    setBusy(row.section);
    try {
      const r = await api.cimSectionBuild(engagementId, row.section, body);
      toast({ type: 'success', title: `§${row.section} ${label} started`, body: scLockedNote(r) });
      setRevise(null);
      setLockAsk(null);
      await load();
    } catch (e) {
      const x = _scErr(e);
      const lock = scLockOf(x);
      if (lock) {
        setRevise(null);
        askLock(lock, 'Lock numbers', scLockConfirm(label, row.section), (more) => start(row, { ...body, ...more }, label));
      } else {
        toast({ type: 'error', title: `Could not start the ${label}`, body: x.detail });
        if (x.status === 409 || x.status === 404) load();
      }
    } finally {
      setBusy(null);
    }
  };

  // Build all (sections null) or Build selected: one chain, Zach's order. One
  // dialog: the lock drawer when the numbers need locking, else the confirm.
  const buildChain = async (sections, more = null) => {
    const n = sections ? sections.length : ((P && P.sections) || []).filter((r) => !r.suppressed).length;
    const title = `Build ${_scPlural(n, 'section')} in order?`;
    const confirmLabel = sections ? 'Build selected' : 'Build all';
    const again = (x) => buildChain(sections, x);
    if (!more) {
      let lk = null;
      try { lk = api.cimSectionsBuildLock ? await api.cimSectionsBuildLock(engagementId) : null; } catch (_) { lk = null; }
      if (lk && lk.needed) { setLockAsk({ lock: lk, title, confirmLabel, changed: false, retry: again }); return; }
      const ok = window.showConfirm ? await window.showConfirm({ title, confirmLabel, cancelLabel: 'Not now' }) : true;
      if (!ok) return;
    }
    setChainBusy(true);
    try {
      const r = await api.cimSectionsBuildAll(engagementId, { ...(sections ? { sections } : {}), ...(more || {}) });
      toast({ type: 'success', title: 'Build chain started', body: scLockedNote(r) });
      setLockAsk(null);
      setTicked(new Set());
      prevChain.current = null;
      await load();
    } catch (e) {
      const x = _scErr(e);
      const lock = scLockOf(x);
      if (lock) askLock(lock, title, confirmLabel, again);
      else {
        toast({ type: 'error', title: 'Could not start the build chain', body: x.detail });
        if (x.status === 409 || x.status === 404) load();
      }
    } finally {
      setChainBusy(false);
    }
  };
  const chainAction = async (action) => {
    const ch = P && P.chain;
    if (!ch) return;
    if (action === 'cancel') {
      const ok = window.showConfirm ? await window.showConfirm({ title: 'Cancel this build chain?', confirmLabel: 'Cancel chain', cancelLabel: 'Keep it', danger: true }) : true;
      if (!ok) return;
    }
    setChainBusy(true);
    try {
      await api.cimChainAction(engagementId, ch.id, action);
      toast({ type: action === 'cancel' ? 'info' : 'success',
        title: { continue: 'Build chain continued', skip: `§${ch.current} skipped`, cancel: 'Build chain cancelled' }[action] });
      prevChain.current = null;
      await load();
    } catch (e) {
      const x = _scErr(e);
      toast({ type: 'error', title: 'Could not change the build chain', body: x.detail });
      if (x.status === 409 || x.status === 404) load();
    } finally {
      setChainBusy(false);
    }
  };
  // Approve clean: every section with nothing to acknowledge, confirmed by name; skips come back with why.
  const approveClean = async () => {
    const picked = scCleanRows(P);
    if (!picked.length) return;
    const ok = window.showConfirm ? await window.showConfirm({
      title: `Approve ${_scPlural(picked.length, 'clean section')}?`, body: scCleanLine(picked),
      confirmLabel: 'Approve', cancelLabel: 'Not now',
    }) : true;
    if (!ok) return;
    setChainBusy(true);
    try {
      toast(scCleanToast(await api.cimSectionsApproveClean(engagementId, scCleanPicks(picked))));
    } catch (e) {
      toast({ type: 'error', title: 'Could not approve', body: _scErr(e).detail });
    } finally {
      await load();
      setChainBusy(false);
    }
  };
  const toggle = (section) => setTicked((cur) => {
    const next = new Set(cur);
    if (next.has(section)) next.delete(section); else next.add(section);
    return next;
  });
  // Assign / unassign: each section's open task is its assignment.
  const assign = async (body) => {
    const secs = body.sections || [];
    const what = secs.length === 1 ? `§${parseInt(secs[0], 10)}` : _scPlural(secs.length, 'section');
    const person = body.assignee_id && ((P && P.assignees) || []).find((p) => p.id === body.assignee_id);
    setAssignBusy(true);
    try {
      const r = await api.cimSectionAssign(engagementId, body);
      const changed = ((r && r.created) || []).length + ((r && r.moved) || []).length + ((r && r.cancelled) || []).length;
      toast(!changed ? { type: 'info', title: 'Nothing changed' }
        : body.assignee_id ? { type: 'success', title: `${what} assigned${person ? ` to ${person.name}` : ''}` }
          : { type: 'success', title: `${what} unassigned` });
      setAssigning(null);
      if (secs.length > 1) setTicked(new Set());
      await load();
    } catch (e) {
      const x = _scErr(e);
      toast({ type: 'error', title: body.assignee_id ? `Could not assign ${what}` : `Could not unassign ${what}`, body: x.detail });
      if (x.status === 409 || x.status === 404) load();
    } finally {
      setAssignBusy(false);
    }
  };

  if (err && !P) {
    return (
      <div style={{ ..._scCard, padding: '24px 18px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text)' }}>{err.detail}</div>
        {err.status !== 404 && err.status !== 422 && <window.Btn size="sm" variant="outline" onClick={() => load()}>Try again</window.Btn>}
      </div>
    );
  }
  if (!P) return <window.SkeletonRows rows={10} />;

  const rows = P.sections || [];
  const chapter = (s) => (chapterOf ? chapterOf(s) : null);
  if (open) {
    return (
      <CimSectionWorkspace key={open.section} engagementId={engagementId} section={open.section} versionNo={open.v} ctx={P}
        row={rows.find((r) => r.section === open.section) || null} chapter={chapter(open.section)} phone={phone}
        onBack={() => go(null)} onVersion={(v) => go({ section: open.section, v })} onChanged={load}
        onGoTab={onGoTab} onOpenHandoff={onOpenHandoff} />
    );
  }

  const global = scGate(P, {}, 'build', null);
  const snap = P.snapshot;
  const rowActions = (r) => {
    const b = scGate(P, r, 'build', null);
    const rv = scGate(P, r, 'revise', r.latest);
    const rr = scRerenderOf(r);
    return [
      { key: 'build', label: 'Build', why: busy === r.section ? 'Starting' : b.why, onClick: () => start(r, { mode: 'full' }, 'build') },
      { key: 'revise', label: 'Revise with instructions', why: busy === r.section ? 'Starting' : rv.why, onClick: () => setRevise(r) },
      rr ? { key: 'rerender', label: scRerenderLabel(P.snapshot), why: busy === r.section ? 'Starting' : scGate(P, r, 'rerender', rr).why,
        onClick: () => start(r, { mode: 'rerender', from_version_id: rr.id }, 're-render') } : null,
    ].filter(Boolean);
  };
  const tickable = rows.filter((r) => !r.suppressed).map((r) => r.section);
  const cleanRows = scCleanRows(P);
  const picked = scTickedOrder(ticked, P.build_order);
  const allGate = scChainGate(P);
  const pickGate = scChainGate(P, new Set(picked));
  const tickBox = (r) => (
    <input type="checkbox" checked={ticked.has(r.section)} disabled={r.suppressed} onChange={() => toggle(r.section)}
      aria-label={`Select §${r.section}`} title={r.suppressed ? 'Suppressed' : undefined}
      style={{ margin: 0, width: phone ? 18 : 14, height: phone ? 18 : 14, cursor: r.suppressed ? 'default' : 'pointer' }} />
  );
  const stateCell = (r) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', minWidth: 0 }}>
      <ScPill map={SC_STATE} k={r.state} dot />
      <ScExit label={r.exit_label} />
      <ScScore score={r.min_score} label="Lowest page score" />
      {r.latest && <ScDot ok={!!r.latest.gates_passed} label={scGatesLabel(!!r.latest.gates_passed, r.latest.failing)} />}
      {r.suppressed && r.suppressed_reason && <span title={r.suppressed_reason} style={{ fontSize: 11.5, color: 'var(--text-4)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180 }}>{r.suppressed_reason}</span>}
    </div>
  );
  const dataCell = (r) => {
    const d = r.readiness;
    if (!d || !d.required) return <span style={{ color: 'var(--text-4)', fontSize: 12 }}>—</span>;
    const full = d.present >= d.required;
    const label = `${d.present}/${d.required}`;
    if (full) return <span style={{ fontSize: 12, color: 'var(--green-fg)', fontVariantNumeric: 'tabular-nums' }}>{label}</span>;
    return (
      <ScPop label={`Missing data, §${r.section}`} phone={phone} trigger={<span style={{ fontSize: 12, color: 'var(--amber-fg)', fontVariantNumeric: 'tabular-nums', textDecoration: 'underline dotted', textUnderlineOffset: 2 }}>{label}</span>}>
        {(d.missing || []).map((m) => (
          <div key={m.block} style={{ padding: '2px 0' }}>
            <span style={{ color: 'var(--text)' }}>{m.block}</span>
            {(m.keys || []).length > 0 && <span style={_scMono}> {m.keys.join(', ')}</span>}
          </div>
        ))}
      </ScPop>
    );
  };
  const orderPill = (r) => <window.Pill hue="slate" sz="sm" title="Build order">#{r.build_order}</window.Pill>;
  const openBtn = (r) => <window.Btn size="sm" variant="outline" onClick={() => go({ section: r.section, v: null })}>Open</window.Btn>;
  // The section's assignment (its open task): who and when; a click opens the Assign drawer.
  const assignCell = (r) => {
    const a = r.assignment;
    const n = parseInt(r.section, 10);
    const can = scAssignGate(P, [r]).ok;
    if (!a) {
      if (!can) return <span style={{ color: 'var(--text-4)', fontSize: 12 }}>—</span>;
      if (phone) return <window.Btn size="sm" variant="outline" onClick={() => setAssigning([r])}>Assign</window.Btn>;
      return <button type="button" onClick={() => setAssigning([r])} aria-label={`Assign §${n}`} style={{ ..._scLink, fontSize: 12, color: 'var(--text-3)' }}>Assign</button>;
    }
    const day = window.fmtTaskDue ? window.fmtTaskDue(a.due_date) : scDue(a.due_date);
    const tip = `${a.assignee || 'Assigned'} · ${a.overdue ? `Overdue, was due ${day}` : `Due ${day}`}`;
    const body = (
      <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0, textAlign: 'left' }}>
        <span style={{ fontSize: 12.5, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.assignee || 'Assigned'}</span>
        <span style={{ fontSize: 11, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', color: a.overdue ? 'var(--rose-fg)' : 'var(--text-4)',
          fontWeight: a.overdue ? 600 : 400 }}>{scDueLine(a)}</span>
      </span>
    );
    return can ? (
      <button type="button" onClick={() => setAssigning([r])} title={tip} aria-label={`§${n}: ${tip}. Change the assignment`}
        style={{ ..._scPlain, display: 'block', maxWidth: '100%', minWidth: 0 }}>{body}</button>
    ) : <span title={tip} style={{ display: 'block', minWidth: 0 }}>{body}</span>;
  };
  const pickedRows = rows.filter((r) => ticked.has(r.section));
  const assignPickGate = scAssignGate(P, pickedRows);

  const topBar = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      {snap ? <window.Pill hue="slate" sz="sm" title="The locked numbers sections build on">Locked numbers v{snap.version} · {_scDay(snap.frozen_at)}</window.Pill>
        : <window.Pill hue="slate" sz="sm" title="Building locks them">Numbers not locked</window.Pill>}
      {P.codename && <span style={{ fontSize: 12.5, color: 'var(--text-2)' }}>{P.codename}</span>}
      <span style={{ flex: 1 }} />
      {!global.ok && <span style={{ fontSize: 12, color: 'var(--text-4)' }}>{global.why}</span>}
      {P.can_approve && cleanRows.length > 0 && (
        <window.Btn size="sm" variant="primary" disabled={chainBusy} onClick={approveClean} title="Nothing to acknowledge: gates pass, every page 9+, nothing newer"
          icon={window.Icon && window.Icon.Check ? <window.Icon.Check size={12} /> : null}>Approve clean ({cleanRows.length})</window.Btn>
      )}
      <ScAct a={{ key: 'build_all', label: 'Build all', why: allGate.why, onClick: () => buildChain(null) }} id="sc-build-all" busy={chainBusy} />
      <ScAct a={{ key: 'build_selected', label: `Build selected${picked.length ? ` (${picked.length})` : ''}`, why: pickGate.why,
        onClick: () => buildChain(picked) }} id="sc-build-selected" busy={chainBusy} />
      <ScAct a={{ key: 'assign_selected', label: `Assign selected${pickedRows.length ? ` (${pickedRows.length})` : ''}`, why: assignPickGate.why,
        onClick: () => setAssigning(pickedRows) }} id="sc-assign-selected" busy={assignBusy} />
    </div>
  );
  const strip = scChainLive(P.chain) && (
    <ScChainStrip chain={P.chain} canRun={!!P.can_run} enabled={!!P.enabled} busy={chainBusy} onAction={chainAction} phone={phone} />
  );

  const list = phone ? (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {rows.map((r) => {
        const upd = scUpdateLines(scRowUpdates(r), P.refs);
        return (
          <div key={r.section} style={{ ..._scCard, padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 8, opacity: r.suppressed ? 0.6 : 1 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <span style={{ paddingTop: 2 }}>{tickBox(r)}</span>
              <span style={{ fontSize: 12, color: 'var(--text-4)', fontVariantNumeric: 'tabular-nums', paddingTop: 2 }}>{r.section}</span>
              <button type="button" onClick={() => go({ section: r.section, v: null })}
                style={{ ..._scPlain, flex: 1, minWidth: 0, textAlign: 'left', fontSize: 14.5, fontWeight: 600, lineHeight: 1.3, color: 'var(--text)' }}>{r.title}</button>
              {orderPill(r)}
            </div>
            {stateCell(r)}
            {upd.length > 0 && <div><ScUpdateChip lines={upd} phone /></div>}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', minWidth: 0 }}>
              <span style={{ ..._scCap, flexShrink: 0 }}>Assignee</span>
              <div style={{ minWidth: 0 }}>{assignCell(r)}</div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {openBtn(r)}
              <ScMenu label="Build" title={`§${r.section} ${r.title}`} items={rowActions(r)} phone />
            </div>
          </div>
        );
      })}
    </div>
  ) : (
    <div style={{ ..._scCard, overflowX: 'auto' }}>
      <div style={{ minWidth: 1152 }}>
        {(() => {
          const COLS = '20px 64px minmax(220px, 1.6fr) 64px minmax(230px, 1.5fr) 130px 104px 140px 150px';
          const head = (label, align) => <span style={{ ..._scCap, textAlign: align || 'left' }}>{label}</span>;
          const all = tickable.length > 0 && tickable.every((x) => ticked.has(x));
          return (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: COLS, gap: 8, alignItems: 'center', padding: '8px 14px', borderBottom: '1px solid var(--border)' }}>
                <input type="checkbox" checked={all} disabled={!tickable.length} aria-label="Select every section"
                  onChange={() => setTicked(all ? new Set() : new Set(tickable))} style={{ margin: 0, width: 14, height: 14, cursor: 'pointer' }} />
                {head('§')}{head('Section')}{head('Data')}{head('State')}{head('Update')}{head('Built')}{head('Assignee')}{head('')}
              </div>
              {rows.map((r, i) => {
                const ch = chapter(r.section);
                const upd = scUpdateLines(scRowUpdates(r), P.refs);
                return (
                  <div key={r.section}
                    style={{ display: 'grid', gridTemplateColumns: COLS, gap: 8, alignItems: 'center', padding: '8px 14px',
                      borderTop: i ? '1px solid var(--border)' : 'none', opacity: r.suppressed ? 0.6 : 1 }}>
                    {tickBox(r)}
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 12, color: 'var(--text-4)', fontVariantNumeric: 'tabular-nums' }}>{r.section}</span>{orderPill(r)}
                    </span>
                    <div style={{ minWidth: 0 }}>
                      <button type="button" onClick={() => go({ section: r.section, v: null })} title={r.title}
                        style={{ ..._scPlain, display: 'block', maxWidth: '100%', fontSize: 12.5, fontWeight: 500, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'left' }}>{r.title}</button>
                      {ch && ch.label && ch.label !== '—' && (
                        <div style={{ fontSize: 11, color: 'var(--text-4)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={ch.label}>{ch.label}</div>
                      )}
                    </div>
                    <div>{dataCell(r)}</div>
                    {stateCell(r)}
                    <div>{upd.length ? <ScUpdateChip lines={upd} /> : <span style={{ color: 'var(--text-4)', fontSize: 12 }}>—</span>}</div>
                    <span style={{ fontSize: 12, color: 'var(--text-3)' }} title={r.latest && r.latest.created_by ? r.latest.created_by : undefined}>
                      {r.latest ? _scWhen(r.latest.created_at) : '—'}
                    </span>
                    <div style={{ minWidth: 0 }}>{assignCell(r)}</div>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      <ScMenu label="Build" title={`§${r.section} build`} items={rowActions(r)} />
                      {openBtn(r)}
                    </div>
                  </div>
                );
              })}
            </>
          );
        })()}
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {topBar}
      {strip}
      {list}
      {revise && (
        <ScReviseDrawer title={`Revise §${revise.section} v${revise.latest ? revise.latest.version : ''}`} busy={busy === revise.section} phone={phone}
          because={scHasEarlier(((revise.update_available || {}).latest) || [])} onClose={() => setRevise(null)}
          onConfirm={(instructions, withBecause) => start(revise, scReviseBody(revise.latest.id, instructions, withBecause), 'revise')} />
      )}
      {lockAsk && (
        <ScLockDrawer title={lockAsk.title} confirmLabel={lockAsk.confirmLabel} lock={lockAsk.lock} changed={lockAsk.changed}
          busy={!!busy || chainBusy} phone={phone} onClose={() => setLockAsk(null)}
          onConfirm={(kinds) => lockAsk.retry({ acknowledge: kinds })}
          onUseLocked={() => lockAsk.retry({ use_locked: true })}
          onLedger={onGoTab ? () => { setLockAsk(null); onGoTab('ledger'); } : null} />
      )}
      {assigning && (
        <ScAssignDrawer rows={assigning} people={P.assignees} me={P.me} busy={assignBusy} phone={phone}
          onClose={() => setAssigning(null)} onConfirm={assign} />
      )}
    </div>
  );
};

Object.assign(window, { CimSectionsTab, CimSectionWorkspace });
